/**
 * Office.js tool executors — the client-side half of the tool-use loop.
 * READ tools return data. WRITE tools modify the workbook.
 * COMPLIANCE tools run the AASB rule engine locally using Office.js-read data.
 */

import { validateAASB116, detectAASB116 } from '../compliance/aasb116';
import { validateAASB16, detectAASB16 } from '../compliance/aasb16';
import { validateAASB15, detectAASB15 } from '../compliance/aasb15';
import { validateAASB137, detectAASB137 } from '../compliance/aasb137';
import { validateAASB102, detectAASB102 } from '../compliance/aasb102';
import { validateAASB119, detectAASB119 } from '../compliance/aasb119';
import { validateAASB138, detectAASB138 } from '../compliance/aasb138';
import { validateAASB136, detectAASB136 } from '../compliance/aasb136';
import { AASB_REGISTRY } from '../compliance/registry';
import { renderPdf, generateWordDocx, generatePowerPoint, downloadBlob, DocSpec } from '../services/documents';
import { validateAASB124, detectAASB124 } from '../compliance/aasb124';
import { validateAASB133, detectAASB133 } from '../compliance/aasb133';
import { validateAASB8, detectAASB8 } from '../compliance/aasb8';
import { validateAASB9, detectAASB9 } from '../compliance/aasb9';
import { validateAASB112, detectAASB112 } from '../compliance/aasb112';
import { validateAASB107, detectAASB107 } from '../compliance/aasb107';
import { validateAASB121, detectAASB121 } from '../compliance/aasb121';
import { validateAASB123, detectAASB123 } from '../compliance/aasb123';
import { validateAASB1058, detectAASB1058 } from '../compliance/aasb1058';
import { generateDisclosureChecklist, ChecklistContext } from '../compliance/disclosureChecklist';
import {
  calculateLandedCost, applyCpiEscalation, nominalToReal,
  bondPrice, modifiedDuration, macaulayDuration, yieldToMaturity,
  testCovenants, npv, irr, payback,
} from '../services/financialMath';

type CellValue = string | number | boolean | null;

const MAX_CELLS_PER_TOOL = 10_000;
const MAX_WRITE_CELLS = 50_000;

// ============= SNAPSHOT STORE (Phase 1: snapshot-and-reference) =============
//
// In-memory cache of sheet data, keyed by snapshot ID. The agent receives
// shape + sample (small) and references the snapshot for downstream compute
// and write — large cell data never re-flows through the LLM context.
//
// This is the same architectural pattern Claude for Excel uses: the model
// reasons over structure and references; raw cells stay local.

interface SheetSnapshot {
  id: string;
  sheet: string;
  address: string;
  rowCount: number;
  columnCount: number;
  values: CellValue[][];
  createdAt: number;
}

const snapshotStore = new Map<string, SheetSnapshot>();
const MAX_SNAPSHOTS = 100;
const SNAPSHOT_TTL_MS = 30 * 60 * 1000; // 30 min

// Tier 2 fix #5 — snapshot-only enforcement.
// Once a sheet is snapshotted in this conversation, raw read tools on that
// sheet are blocked. The agent must use compute_on_snapshot / describe_snapshot
// instead — re-reading a 12K-cell sheet costs ~30K tokens of context every
// turn it stays in history. Cleared by clearAllSnapshots() on "New chat".
const snapshottedSheets = new Set<string>();

function normalizeSheetName(name: string): string {
  return name.trim().toLowerCase();
}

function markSheetSnapshotted(name: string): void {
  snapshottedSheets.add(normalizeSheetName(name));
}

async function resolveTargetSheetName(input?: { sheet?: string }): Promise<string> {
  if (input?.sheet) return input.sheet;
  return Excel.run(async (ctx) => {
    const ws = ctx.workbook.worksheets.getActiveWorksheet();
    ws.load('name');
    await ctx.sync();
    return ws.name;
  });
}

function evictOldSnapshots(): void {
  const now = Date.now();
  for (const [id, s] of snapshotStore) {
    if (now - s.createdAt > SNAPSHOT_TTL_MS) snapshotStore.delete(id);
  }
  if (snapshotStore.size > MAX_SNAPSHOTS) {
    const sorted = Array.from(snapshotStore.entries())
      .sort((a, b) => a[1].createdAt - b[1].createdAt);
    const toRemove = sorted.slice(0, snapshotStore.size - MAX_SNAPSHOTS);
    for (const [id] of toRemove) snapshotStore.delete(id);
  }
}

function newSnapshotId(): string {
  return 'snap_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
}

function getSnapshot(id: string): SheetSnapshot | null {
  evictOldSnapshots();
  return snapshotStore.get(id) ?? null;
}

/** Clear all snapshots (called by frontend on "New chat"). */
export function clearAllSnapshots(): void {
  snapshotStore.clear();
  snapshottedSheets.clear();
}

// ---- Range spec parsers ----

/** Parse a row spec like "5..18" or "5,7,9" or "5..18,22..30" into 1-based row indices. */
function parseRowSpec(spec: string): number[] {
  if (!spec) return [];
  const parts = spec.split(',').map(s => s.trim());
  const rows: number[] = [];
  for (const part of parts) {
    if (part.includes('..')) {
      const [a, b] = part.split('..').map(Number);
      for (let i = a; i <= b; i++) rows.push(i);
    } else {
      rows.push(Number(part));
    }
  }
  return rows.filter(n => Number.isFinite(n) && n > 0);
}

/** Parse a col spec like "B..K" or "B,D,F" into 0-based col indices. */
function parseColSpec(spec: string): number[] {
  if (!spec) return [];
  const parts = spec.split(',').map(s => s.trim());
  const cols: number[] = [];
  for (const part of parts) {
    if (part.includes('..')) {
      const [a, b] = part.split('..');
      for (let i = letterToCol(a); i <= letterToCol(b); i++) cols.push(i);
    } else if (/^[A-Z]+$/i.test(part)) {
      cols.push(letterToCol(part));
    } else {
      cols.push(Number(part));
    }
  }
  return cols.filter(n => Number.isFinite(n) && n >= 0);
}

function letterToCol(letters: string): number {
  let n = 0;
  for (const ch of letters.toUpperCase()) {
    n = n * 26 + (ch.charCodeAt(0) - 64);
  }
  return n - 1; // 0-based
}

function round(n: number, places = 4): number {
  return Math.round(n * Math.pow(10, places)) / Math.pow(10, places);
}

interface ToolResult {
  ok: boolean;
  data?: unknown;
  error?: string;
}

function truncateValues(values: CellValue[][]): { values: CellValue[][]; truncated: boolean } {
  if (!values.length) return { values, truncated: false };
  const total = values.length * values[0].length;
  if (total <= MAX_CELLS_PER_TOOL) return { values, truncated: false };
  const cols = values[0].length;
  const maxRows = Math.max(1, Math.floor(MAX_CELLS_PER_TOOL / cols));
  return { values: values.slice(0, maxRows), truncated: true };
}

// ---------- READ ----------

async function getWorkbookInfo(): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const wb = ctx.workbook;
    const sheet = wb.worksheets.getActiveWorksheet();
    const sel = wb.getSelectedRange();
    wb.load('name');
    sheet.load('name');
    sel.load('address');
    await ctx.sync();
    return {
      ok: true,
      data: {
        workbookName: wb.name,
        activeSheet: sheet.name,
        currentSelection: sel.address.split('!').pop() ?? sel.address,
      },
    };
  });
}

async function listSheets(): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const sheets = ctx.workbook.worksheets;
    sheets.load('items/name');
    await ctx.sync();
    const out = [];
    for (const s of sheets.items) {
      const used = s.getUsedRangeOrNullObject();
      used.load(['address', 'rowCount', 'columnCount']);
      await ctx.sync();
      if (used.isNullObject) {
        out.push({ name: s.name, usedRange: null, rowCount: 0, columnCount: 0 });
      } else {
        out.push({
          name: s.name,
          usedRange: used.address.split('!').pop() ?? used.address,
          rowCount: used.rowCount,
          columnCount: used.columnCount,
        });
      }
    }
    return { ok: true, data: { sheets: out } };
  });
}

async function readSelection(): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const range = ctx.workbook.getSelectedRange();
    const sheet = range.worksheet;
    range.load(['address', 'values', 'rowCount', 'columnCount']);
    sheet.load('name');
    await ctx.sync();
    if (range.rowCount === 0 || range.columnCount === 0) {
      return { ok: false, error: 'No range is selected.' };
    }
    const { values, truncated } = truncateValues(range.values as CellValue[][]);
    return {
      ok: true,
      data: {
        sheet: sheet.name,
        address: range.address.split('!').pop() ?? range.address,
        rowCount: range.rowCount,
        columnCount: range.columnCount,
        values,
        truncated,
      },
    };
  });
}

async function readRange(input: { sheet?: string; address: string }): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const sheet = input.sheet
      ? ctx.workbook.worksheets.getItemOrNullObject(input.sheet)
      : ctx.workbook.worksheets.getActiveWorksheet();
    sheet.load('name');
    await ctx.sync();
    if ('isNullObject' in sheet && (sheet as any).isNullObject) {
      return { ok: false, error: `Sheet not found: ${input.sheet}` };
    }
    try {
      const range = sheet.getRange(input.address);
      range.load(['address', 'values', 'rowCount', 'columnCount']);
      await ctx.sync();
      const { values, truncated } = truncateValues(range.values as CellValue[][]);
      return {
        ok: true,
        data: {
          sheet: sheet.name,
          address: range.address.split('!').pop() ?? range.address,
          rowCount: range.rowCount,
          columnCount: range.columnCount,
          values,
          truncated,
        },
      };
    } catch (e) {
      return { ok: false, error: `Could not read range ${input.address}: ${(e as Error).message}` };
    }
  });
}

async function readUsedRange(input: { sheet?: string }): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const sheet = input.sheet
      ? ctx.workbook.worksheets.getItemOrNullObject(input.sheet)
      : ctx.workbook.worksheets.getActiveWorksheet();
    sheet.load('name');
    await ctx.sync();
    if ('isNullObject' in sheet && (sheet as any).isNullObject) {
      return { ok: false, error: `Sheet not found: ${input.sheet}` };
    }
    const used = sheet.getUsedRangeOrNullObject();
    used.load(['address', 'values', 'rowCount', 'columnCount']);
    await ctx.sync();
    if (used.isNullObject) {
      return { ok: true, data: { sheet: sheet.name, address: null, rowCount: 0, columnCount: 0, values: [], truncated: false } };
    }
    const { values, truncated } = truncateValues(used.values as CellValue[][]);
    return {
      ok: true,
      data: {
        sheet: sheet.name,
        address: used.address.split('!').pop() ?? used.address,
        rowCount: used.rowCount,
        columnCount: used.columnCount,
        values,
        truncated,
      },
    };
  });
}

// ---------- SNAPSHOT (Phase 1) ----------

/**
 * Read a sheet (or range) into the snapshot store and return only metadata
 * + a small sample. The agent uses the returned snapshotId for subsequent
 * compute / write operations — large cell data never re-enters the LLM
 * context.
 */
async function snapshotSheetTool(input: { sheet?: string; address?: string }): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const sheet = input.sheet
      ? ctx.workbook.worksheets.getItemOrNullObject(input.sheet)
      : ctx.workbook.worksheets.getActiveWorksheet();
    sheet.load('name');
    await ctx.sync();
    if ('isNullObject' in sheet && (sheet as any).isNullObject) {
      return { ok: false, error: `Sheet not found: ${input.sheet}` };
    }

    let range: Excel.Range;
    if (input.address) {
      range = sheet.getRange(input.address);
    } else {
      const used = sheet.getUsedRangeOrNullObject();
      used.load(['address']);
      await ctx.sync();
      if (used.isNullObject) {
        return {
          ok: true,
          data: {
            snapshotId: null,
            sheet: sheet.name,
            address: null,
            shape: { rows: 0, cols: 0 },
            note: 'Sheet is empty; no snapshot created.',
          },
        };
      }
      range = sheet.getRange(used.address.split('!').pop() ?? used.address);
    }

    range.load(['address', 'values', 'rowCount', 'columnCount']);
    await ctx.sync();

    const values = range.values as CellValue[][];
    const rowCount = range.rowCount;
    const columnCount = range.columnCount;

    const id = newSnapshotId();
    snapshotStore.set(id, {
      id,
      sheet: sheet.name,
      address: range.address.split('!').pop() ?? range.address,
      rowCount,
      columnCount,
      values,
      createdAt: Date.now(),
    });
    evictOldSnapshots();

    // Sample = first 3 rows + last 1 row, for orientation
    const sample = [
      ...values.slice(0, Math.min(3, rowCount)),
      ...(rowCount > 4 ? [values[rowCount - 1]] : []),
    ];

    // Per-column type/stats summary
    const columnSummaries = Array.from({ length: columnCount }, (_, c) => {
      let numeric = 0, blanks = 0, strings = 0;
      let sum = 0, min = Infinity, max = -Infinity;
      for (let r = 0; r < rowCount; r++) {
        const v = values[r]?.[c];
        if (v === null || v === undefined || v === '') { blanks++; continue; }
        if (typeof v === 'number') {
          numeric++; sum += v;
          if (v < min) min = v;
          if (v > max) max = v;
        } else if (typeof v === 'string') {
          strings++;
        }
      }
      const dominantType = numeric >= strings && numeric >= blanks
        ? 'number' : strings >= blanks ? 'string' : 'blank';
      const out: Record<string, unknown> = {
        col: c,
        header: typeof values[0]?.[c] === 'string' ? values[0][c] : null,
        dominantType,
        nullCount: blanks,
        numericCount: numeric,
        stringCount: strings,
      };
      if (numeric > 0) {
        out.mean = round(sum / numeric);
        out.min = round(min);
        out.max = round(max);
        out.sum = round(sum);
      }
      return out;
    });

    return {
      ok: true,
      data: {
        snapshotId: id,
        sheet: sheet.name,
        address: range.address.split('!').pop() ?? range.address,
        shape: { rows: rowCount, cols: columnCount },
        sample,
        columnSummaries,
        ttlMinutes: 30,
        usage: 'Pass snapshotId to compute_on_snapshot or write_from_snapshot. Use describe_snapshot to find header/total rows. Cell data lives in frontend cache; do not request the full grid unless absolutely needed.',
      },
    };
  });
}

// ============= DASHBOARD SKILL — structure detection =============
//
// Scans a sheet for the structural ingredients a dashboard needs:
//   - subtotal rows (ALL-CAPS labels in column A with a numeric value)
//   - time-series columns (date headers, typically row 2 from column B)
//   - semantic groupings (revenue / expense / profit subtotals)
//   - confidence + ambiguities — surfaces what the agent should ask the user
//
// Generic across industries — no hardcoded labels. The agent uses this
// result + a clarifying question to design the dashboard, then builds in
// 5 atomic calls per the DASHBOARD SKILL protocol in the system prompt.

function colNumToLetter(c: number): string {
  let s = '';
  while (c > 0) {
    const r = (c - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    c = Math.floor((c - 1) / 26);
  }
  return s;
}

function classifySubtotal(label: string): 'revenue' | 'expense' | 'profit' | 'other' {
  const u = label.toUpperCase();
  // Profit terms first — they often contain "INCOME" (e.g. NET INCOME) and
  // would otherwise misclassify as revenue.
  if (/\b(EBITDA|EBIT|NET INCOME|NET PROFIT|NET LOSS|OPERATING PROFIT|GROSS PROFIT|SURPLUS|DEFICIT|RESULT|BOTTOM LINE)\b/.test(u)) {
    return 'profit';
  }
  if (/\b(EXPENSE|EXPENSES|COSTS?|OUTGOING|OUTGOINGS|OPEX|CAPEX|DEPRECIATION|AMORTI[SZ]ATION|CHARGES|FUNDRAISING|MAINTENANCE|REPAIRS|ADMINISTRATION|MARKETING|TRAVEL|TRANSPORT|EMPLOYEE|SALARIES|WAGES|AGENCY|CONTRACTORS|INTERNAL CHARGES)\b/.test(u)) {
    return 'expense';
  }
  if (/\b(REVENUE|INCOME|SALES|FEES|TURNOVER|RECEIPTS|GRANTS|SUBSIDIES|DONATIONS|FUNDING)\b/.test(u)) {
    return 'revenue';
  }
  return 'other';
}

interface DashboardSubtotal {
  label: string;
  row: number;
  latest_value: number | null;
  semantic_class: 'revenue' | 'expense' | 'profit' | 'other';
}

interface DashboardTimeColumn {
  col: string;
  label: string;
  parsed_date?: string;
}

async function scanForDashboardTool(input: {
  sheet?: string;
  maxRows?: number;
}): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const sheet = input.sheet
      ? ctx.workbook.worksheets.getItemOrNullObject(input.sheet)
      : ctx.workbook.worksheets.getActiveWorksheet();
    sheet.load('name');
    await ctx.sync();
    if ('isNullObject' in sheet && (sheet as any).isNullObject) {
      return { ok: false, error: `Sheet not found: ${input.sheet}` };
    }

    const maxRows = Math.min(input.maxRows ?? 500, 1000);

    // Read column A for labels, top 3 rows for time headers, B..O across
    // all candidate rows so we can pick the latest numeric value per row.
    const labelRange = sheet.getRange(`A1:A${maxRows}`);
    labelRange.load(['values']);
    const headerRange = sheet.getRange('A1:O3');
    headerRange.load(['values']);
    const numRange = sheet.getRange(`B1:O${maxRows}`);
    numRange.load(['values']);
    await ctx.sync();

    const labels = (labelRange.values as CellValue[][]).map(r => r[0]);
    const headerVals = headerRange.values as CellValue[][];
    const numVals = numRange.values as CellValue[][];

    // 1. Detect time-header row. Try row 2 first (most common), then row 1
    //    and row 3 as fallbacks. A row qualifies if ≥3 columns from B
    //    onward look date-like and the run is not interrupted by Total/Sum.
    const timeColumns: DashboardTimeColumn[] = [];
    let timeHeaderRow = 0;
    for (const ri of [1, 0, 2]) {
      const cands: DashboardTimeColumn[] = [];
      const row = headerVals[ri] || [];
      for (let c = 1; c < row.length; c++) {
        const v = row[c];
        if (v == null || v === '') break;
        const s = String(v);
        if (/^(total|actual|year|sum|grand|ytd|var|variance|budget|forecast)/i.test(s.trim())) break;
        let parsed: string | undefined;
        if ((v as unknown) instanceof Date) {
          parsed = (v as unknown as Date).toISOString().slice(0, 10);
        } else if (typeof v === 'number' && v > 30000 && v < 80000) {
          // Excel date serial in plausible range (1982–2089)
          const ms = (v - 25569) * 86400000;
          const d = new Date(ms);
          if (!isNaN(d.getTime())) parsed = d.toISOString().slice(0, 10);
        } else if (typeof v === 'string') {
          if (/^\d{4}-\d{1,2}-\d{1,2}/.test(s) || /^[A-Z][a-z]{2}[- ]?\d{2,4}$/i.test(s.trim())) {
            parsed = s.trim();
          }
        }
        cands.push({ col: colNumToLetter(c + 1), label: s, parsed_date: parsed });
      }
      if (cands.length >= 3) {
        timeColumns.push(...cands);
        timeHeaderRow = ri + 1;
        break;
      }
    }

    // 2. Detect subtotal rows. A subtotal candidate is an ALL-CAPS line in
    //    column A with ≥4 letters that has at least one non-zero numeric
    //    value across B..O.
    const subtotals: DashboardSubtotal[] = [];
    for (let i = 0; i < labels.length; i++) {
      const v = labels[i];
      if (typeof v !== 'string') continue;
      const s = v.trim();
      if (!s) continue;
      const letters = s.replace(/[^A-Za-z]/g, '');
      if (letters.length < 4) continue;
      if (letters !== letters.toUpperCase()) continue;
      const rowVals = numVals[i] || [];
      let latest: number | null = null;
      let hasNumeric = false;
      for (let c = rowVals.length - 1; c >= 0; c--) {
        const x = rowVals[c];
        if (typeof x === 'number') {
          if (latest == null) latest = x;
          if (x !== 0) hasNumeric = true;
        }
      }
      if (!hasNumeric) continue;
      subtotals.push({
        label: s,
        row: i + 1,
        latest_value: latest,
        semantic_class: classifySubtotal(s),
      });
    }

    // 3. Suggested groupings
    const grouping = {
      revenue_like: subtotals.filter(s => s.semantic_class === 'revenue').map(s => s.label),
      expense_like: subtotals.filter(s => s.semantic_class === 'expense').map(s => s.label),
      profit_like: subtotals.filter(s => s.semantic_class === 'profit').map(s => s.label),
      other: subtotals.filter(s => s.semantic_class === 'other').map(s => s.label),
    };

    // 4. Confidence + ambiguities
    const ambiguities: string[] = [];
    let confidence: 'high' | 'medium' | 'low' = 'high';
    if (subtotals.length < 3) {
      confidence = 'low';
      ambiguities.push(
        `Only ${subtotals.length} subtotal candidate(s) detected. Sheet may not use ALL-CAPS labels in column A.`,
      );
    }
    if (timeColumns.length < 2) {
      confidence = 'low';
      ambiguities.push(
        'No time-period header row detected. Expected dates in row 1, 2, or 3 from column B onward.',
      );
    }
    if (grouping.revenue_like.length === 0) {
      confidence = confidence === 'high' ? 'medium' : confidence;
      ambiguities.push(
        'No revenue-like subtotal detected. Confirm with the user which row represents revenue/income.',
      );
    }
    if (grouping.profit_like.length === 0) {
      confidence = confidence === 'high' ? 'medium' : confidence;
      ambiguities.push(
        'No profit-like subtotal detected. Confirm with the user which row represents the bottom-line result (EBITDA / Net Income / Surplus).',
      );
    }
    if (grouping.expense_like.length > 3) {
      ambiguities.push(
        `Multiple expense subtotals detected (${grouping.expense_like.slice(0, 5).join(', ')}${grouping.expense_like.length > 5 ? ', …' : ''}). Confirm which is the grand total vs. which are sub-categories.`,
      );
    }

    return {
      ok: true,
      data: {
        sheet: sheet.name,
        subtotals,
        time_columns: timeColumns,
        time_header_row: timeHeaderRow,
        suggested_groupings: grouping,
        confidence,
        ambiguities,
        notes:
          'Use these subtotal rows for INDEX/MATCH formulas in the dashboard. The dashboard skill in the system prompt has the build protocol — do not skip steps.',
      },
    };
  });
}

/**
 * Return derived structure for a snapshot: blank rows, likely header rows,
 * likely total/subtotal rows. Used by the agent to plan operations without
 * round-tripping cell data through the LLM.
 */
async function describeSnapshotTool(input: { snapshotId: string }): Promise<ToolResult> {
  const snap = getSnapshot(input.snapshotId);
  if (!snap) return { ok: false, error: `Snapshot ${input.snapshotId} not found or expired` };

  const blankRows: number[] = [];
  const headerRows: number[] = [];
  const totalRows: number[] = [];
  const sectionStarts: { row: number; label: string }[] = [];

  for (let r = 0; r < snap.rowCount; r++) {
    const row = snap.values[r];
    const allBlank = row.every(v => v === null || v === undefined || v === '');
    if (allBlank) { blankRows.push(r + 1); continue; }

    const stringCount = row.filter(v => typeof v === 'string' && v !== '').length;
    const numberCount = row.filter(v => typeof v === 'number').length;
    const firstCell = row[0];

    // Likely a section header (mostly text, few numbers)
    if (stringCount >= 1 && numberCount === 0 && typeof firstCell === 'string' && firstCell.length > 0) {
      headerRows.push(r + 1);
      sectionStarts.push({ row: r + 1, label: firstCell.trim() });
      continue;
    }

    // Likely a total/subtotal row
    if (typeof firstCell === 'string' && /\b(total|sum|subtotal|grand|net|gross|ebitda|operating)\b/i.test(firstCell)) {
      totalRows.push(r + 1);
    }
  }

  return {
    ok: true,
    data: {
      snapshotId: snap.id,
      shape: { rows: snap.rowCount, cols: snap.columnCount },
      blankRowsCount: blankRows.length,
      blankRows: blankRows.slice(0, 50),
      headerRowsCount: headerRows.length,
      headerRows: headerRows.slice(0, 30),
      totalRowsCount: totalRows.length,
      totalRows: totalRows.slice(0, 30),
      sections: sectionStarts.slice(0, 20),
    },
  };
}

/**
 * Run a computation on a cached snapshot — returns just the result number(s),
 * not the cell data that produced them.
 */
async function computeOnSnapshotTool(input: {
  snapshotId: string;
  op: 'row_average' | 'row_sum' | 'row_min' | 'row_max' | 'col_average' | 'col_sum' | 'project_average' | 'percent_change' | 'cell_value';
  rows?: string;
  cols?: string;
  baseRow?: number;
  baseCol?: number;
}): Promise<ToolResult> {
  const snap = getSnapshot(input.snapshotId);
  if (!snap) return { ok: false, error: `Snapshot ${input.snapshotId} not found or expired` };

  const rows = input.rows ? parseRowSpec(input.rows) : Array.from({ length: snap.rowCount }, (_, i) => i + 1);
  const cols = input.cols ? parseColSpec(input.cols) : Array.from({ length: snap.columnCount }, (_, i) => i);

  const collectRow = (r: number): number[] => {
    const out: number[] = [];
    for (const c of cols) {
      const v = snap.values[r - 1]?.[c];
      if (typeof v === 'number') out.push(v);
    }
    return out;
  };
  const collectCol = (c: number): number[] => {
    const out: number[] = [];
    for (const r of rows) {
      const v = snap.values[r - 1]?.[c];
      if (typeof v === 'number') out.push(v);
    }
    return out;
  };

  switch (input.op) {
    case 'row_average':
    case 'row_sum':
    case 'row_min':
    case 'row_max':
    case 'project_average': {
      const results = rows.map(r => {
        const cells = collectRow(r);
        if (cells.length === 0) return { row: r, value: 0, n: 0 };
        let value = 0;
        if (input.op === 'row_sum') value = cells.reduce((a, b) => a + b, 0);
        else if (input.op === 'row_min') value = Math.min(...cells);
        else if (input.op === 'row_max') value = Math.max(...cells);
        else value = cells.reduce((a, b) => a + b, 0) / cells.length; // average / project_average
        return { row: r, value: round(value), n: cells.length };
      });
      return { ok: true, data: { op: input.op, count: results.length, results } };
    }
    case 'col_average':
    case 'col_sum': {
      const results = cols.map(c => {
        const cells = collectCol(c);
        if (cells.length === 0) return { col: c, value: 0, n: 0 };
        const value = input.op === 'col_sum'
          ? cells.reduce((a, b) => a + b, 0)
          : cells.reduce((a, b) => a + b, 0) / cells.length;
        return { col: c, value: round(value), n: cells.length };
      });
      return { ok: true, data: { op: input.op, count: results.length, results } };
    }
    case 'percent_change': {
      // For each row, % change from the FIRST col in `cols` to the LAST col
      const fromCol = cols[0];
      const toCol = cols[cols.length - 1];
      const results = rows.map(r => {
        const from = snap.values[r - 1]?.[fromCol];
        const to = snap.values[r - 1]?.[toCol];
        if (typeof from === 'number' && typeof to === 'number' && from !== 0) {
          return { row: r, from: round(from), to: round(to), pctChange: round((to - from) / from) };
        }
        return { row: r, from: from as number | null, to: to as number | null, pctChange: null };
      });
      return { ok: true, data: { op: 'percent_change', fromCol, toCol, results } };
    }
    case 'cell_value': {
      const r = input.baseRow ?? 1;
      const c = input.baseCol ?? 0;
      const v = snap.values[r - 1]?.[c];
      return { ok: true, data: { op: 'cell_value', row: r, col: c, value: v } };
    }
    default:
      return { ok: false, error: `Unknown op: ${input.op}` };
  }
}

/**
 * Write data DERIVED from a snapshot to a target sheet. Frontend computes
 * locally and writes via Office.js — large data never round-trips through
 * the LLM context.
 */
async function writeFromSnapshotTool(input: {
  snapshotId: string;
  targetSheet: string;
  targetStartCell: string;
  mode: 'project_average' | 'copy' | 'col_sum' | 'col_average' | 'percent_change' | 'formula_per_row';
  rows?: string;
  cols?: string;
  formulaTemplate?: string;
}): Promise<ToolResult> {
  const snap = getSnapshot(input.snapshotId);
  if (!snap) return { ok: false, error: `Snapshot ${input.snapshotId} not found or expired` };

  const rowsSpec = input.rows ? parseRowSpec(input.rows) : Array.from({ length: snap.rowCount }, (_, i) => i + 1);
  const colsSpec = input.cols ? parseColSpec(input.cols) : Array.from({ length: snap.columnCount }, (_, i) => i);

  let writeValues: CellValue[][] = [];

  switch (input.mode) {
    case 'project_average': {
      // One cell per row: the average of selected cols
      writeValues = rowsSpec.map(r => {
        const cells: number[] = [];
        for (const c of colsSpec) {
          const v = snap.values[r - 1]?.[c];
          if (typeof v === 'number') cells.push(v);
        }
        return [cells.length ? round(cells.reduce((a, b) => a + b, 0) / cells.length) : 0];
      });
      break;
    }
    case 'col_sum': {
      writeValues = [colsSpec.map(c => {
        const cells: number[] = [];
        for (const r of rowsSpec) {
          const v = snap.values[r - 1]?.[c];
          if (typeof v === 'number') cells.push(v);
        }
        return cells.length ? round(cells.reduce((a, b) => a + b, 0)) : 0;
      })];
      break;
    }
    case 'col_average': {
      writeValues = [colsSpec.map(c => {
        const cells: number[] = [];
        for (const r of rowsSpec) {
          const v = snap.values[r - 1]?.[c];
          if (typeof v === 'number') cells.push(v);
        }
        return cells.length ? round(cells.reduce((a, b) => a + b, 0) / cells.length) : 0;
      })];
      break;
    }
    case 'copy': {
      writeValues = rowsSpec.map(r => colsSpec.map(c => snap.values[r - 1]?.[c] ?? null));
      break;
    }
    case 'percent_change': {
      const fromCol = colsSpec[0];
      const toCol = colsSpec[colsSpec.length - 1];
      writeValues = rowsSpec.map(r => {
        const from = snap.values[r - 1]?.[fromCol];
        const to = snap.values[r - 1]?.[toCol];
        if (typeof from === 'number' && typeof to === 'number' && from !== 0) {
          return [round((to - from) / from)];
        }
        return [0];
      });
      break;
    }
    case 'formula_per_row': {
      // Generate a formula PER ROW from a template — frontend expands
      // {row} → actual row number. ONE tool call writes N formulas.
      // This is the killer feature: instead of the agent making 30 separate
      // add_formulas calls (which blow the conversation), it makes ONE
      // write_from_snapshot call and the frontend produces all the formulas.
      if (!input.formulaTemplate) {
        return { ok: false, error: `formula_per_row mode requires formulaTemplate (e.g. "=AVERAGE(Sheet1!B{row}:K{row})")` };
      }
      // Will be written as formulas, not values — Excel evaluates on write.
      writeValues = rowsSpec.map(r => {
        const formula = input.formulaTemplate!.replace(/\{row\}/g, String(r));
        return [formula as CellValue];
      });
      break;
    }
    default:
      return { ok: false, error: `Unsupported mode: ${input.mode}` };
  }

  return Excel.run(async (ctx) => {
    let target = ctx.workbook.worksheets.getItemOrNullObject(input.targetSheet);
    target.load('name');
    await ctx.sync();
    if ('isNullObject' in target && (target as any).isNullObject) {
      target = ctx.workbook.worksheets.add(input.targetSheet);
    }
    const startRange = target.getRange(input.targetStartCell);
    const range = startRange.getResizedRange(
      writeValues.length - 1,
      (writeValues[0]?.length ?? 1) - 1,
    );
    if (input.mode === 'formula_per_row') {
      // formula_per_row mode wrote into writeValues but they're formula strings
      range.formulas = writeValues as any;
    } else {
      range.values = writeValues as any;
    }
    range.load('address');
    await ctx.sync();

    return {
      ok: true,
      data: {
        sourceSnapshotId: snap.id,
        targetSheet: target.name,
        address: range.address.split('!').pop() ?? range.address,
        cellsWritten: writeValues.length * (writeValues[0]?.length ?? 1),
        mode: input.mode,
      },
    };
  });
}

// ---------- DEEP READ ----------

async function readFormulas(input: { sheet?: string; address?: string }): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const sheet = input.sheet
      ? ctx.workbook.worksheets.getItemOrNullObject(input.sheet)
      : ctx.workbook.worksheets.getActiveWorksheet();
    sheet.load('name');
    await ctx.sync();
    if ('isNullObject' in sheet && (sheet as any).isNullObject) {
      return { ok: false, error: `Sheet not found: ${input.sheet}` };
    }
    let range: Excel.Range;
    if (input.address) {
      range = sheet.getRange(input.address);
    } else {
      const used = sheet.getUsedRangeOrNullObject();
      used.load(['address']);
      await ctx.sync();
      if (used.isNullObject) return { ok: true, data: { sheet: sheet.name, formulas: [], address: null } };
      range = sheet.getRange(used.address.split('!').pop() ?? used.address);
    }
    range.load(['address', 'formulas', 'rowCount', 'columnCount']);
    await ctx.sync();
    const cells = range.rowCount * range.columnCount;
    if (cells > MAX_CELLS_PER_TOOL) {
      return { ok: false, error: `Range too large (${cells} cells, max ${MAX_CELLS_PER_TOOL}).` };
    }
    return {
      ok: true,
      data: {
        sheet: sheet.name,
        address: range.address.split('!').pop() ?? range.address,
        rowCount: range.rowCount,
        columnCount: range.columnCount,
        formulas: range.formulas,
      },
    };
  });
}

/**
 * Compress a 2D number-format array into a summary struct.
 *
 * For an 80×8 P&L where every cell is "[Red]-$#,##0.00", the raw 2D array
 * is ~13 KB of repeated text. The summary captures the distribution in
 * ~500 B. The agent can ask for the full grid with `verbose: true` if it
 * actually needs per-cell formats (rare).
 */
function summarizeNumberFormats(grid: string[][]): {
  totalCells: number;
  distinct: { format: string; count: number; firstCell: string }[];
  byColumn: { col: number; dominantFormat: string; otherFormatCount: number }[];
} {
  const counts = new Map<string, { count: number; firstRow: number; firstCol: number }>();
  const colFormats: Map<string, number>[] = [];

  for (let r = 0; r < grid.length; r++) {
    const row = grid[r];
    for (let c = 0; c < row.length; c++) {
      const fmt = row[c];
      const cur = counts.get(fmt);
      if (cur) cur.count++;
      else counts.set(fmt, { count: 1, firstRow: r, firstCol: c });

      if (!colFormats[c]) colFormats[c] = new Map();
      colFormats[c].set(fmt, (colFormats[c].get(fmt) ?? 0) + 1);
    }
  }

  const distinct = Array.from(counts.entries())
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, 10) // cap at top 10 distinct formats
    .map(([format, info]) => ({
      format,
      count: info.count,
      firstCell: a1(info.firstRow, info.firstCol),
    }));

  const byColumn = colFormats.map((m, col) => {
    const sorted = Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
    const [dominantFormat, dominantCount] = sorted[0] ?? ['General', 0];
    const otherFormatCount = Array.from(m.values()).reduce((s, n) => s + n, 0) - dominantCount;
    return { col, dominantFormat, otherFormatCount };
  });

  const totalCells = grid.reduce((s, row) => s + row.length, 0);
  return { totalCells, distinct, byColumn };
}

function a1(row: number, col: number): string {
  let c = col;
  let letters = '';
  do {
    letters = String.fromCharCode(65 + (c % 26)) + letters;
    c = Math.floor(c / 26) - 1;
  } while (c >= 0);
  return `${letters}${row + 1}`;
}

async function readCellTypes(input: {
  sheet?: string;
  address?: string;
  verbose?: boolean;
}): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const sheet = input.sheet
      ? ctx.workbook.worksheets.getItemOrNullObject(input.sheet)
      : ctx.workbook.worksheets.getActiveWorksheet();
    sheet.load('name');
    await ctx.sync();
    if ('isNullObject' in sheet && (sheet as any).isNullObject) {
      return { ok: false, error: `Sheet not found: ${input.sheet}` };
    }
    let range: Excel.Range;
    if (input.address) {
      range = sheet.getRange(input.address);
    } else {
      const used = sheet.getUsedRangeOrNullObject();
      used.load(['address']);
      await ctx.sync();
      if (used.isNullObject) return { ok: true, data: { sheet: sheet.name, address: null, types: [], values: [] } };
      range = sheet.getRange(used.address.split('!').pop() ?? used.address);
    }
    range.load(['address', 'valueTypes', 'values', 'numberFormat', 'rowCount', 'columnCount']);
    await ctx.sync();
    const cells = range.rowCount * range.columnCount;
    if (cells > MAX_CELLS_PER_TOOL) {
      return { ok: false, error: `Range too large (${cells} cells, max ${MAX_CELLS_PER_TOOL}).` };
    }
    // Default: return a number-format SUMMARY (top-10 distinct + per-column
    // dominant) instead of the full per-cell 2D array. Saves ~95% of the
    // bytes when the sheet has consistent formatting (typical P&L).
    // verbose: true returns the raw 2D array, like the original behaviour.
    const formatPayload = input.verbose
      ? { numberFormat: range.numberFormat as string[][] }
      : { numberFormatSummary: summarizeNumberFormats(range.numberFormat as string[][]) };

    return {
      ok: true,
      data: {
        sheet: sheet.name,
        address: range.address.split('!').pop() ?? range.address,
        rowCount: range.rowCount,
        columnCount: range.columnCount,
        types: range.valueTypes,
        values: range.values,
        ...formatPayload,
      },
    };
  });
}

async function readWorkbookStructure(): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const wb = ctx.workbook;
    const sheets = wb.worksheets;
    const names = wb.names;
    sheets.load('items/name,items/visibility,items/position,items/tabColor');
    names.load('items/name,items/comment,items/value,items/formula,items/scope');
    await ctx.sync();

    const sheetSummaries = [] as any[];
    for (const s of sheets.items) {
      const used = s.getUsedRangeOrNullObject();
      used.load(['address', 'rowCount', 'columnCount']);
      const tables = s.tables;
      tables.load('items/name,items/showHeaders,items/showTotals');
      const charts = s.charts;
      charts.load('items/name,items/chartType');
      await ctx.sync();
      sheetSummaries.push({
        name: s.name,
        position: s.position,
        visibility: s.visibility,
        tabColor: s.tabColor || null,
        usedRange: used.isNullObject ? null : (used.address.split('!').pop() ?? used.address),
        rowCount: used.isNullObject ? 0 : used.rowCount,
        columnCount: used.isNullObject ? 0 : used.columnCount,
        tables: tables.items.map(t => ({ name: t.name, showHeaders: t.showHeaders, showTotals: t.showTotals })),
        charts: charts.items.map(c => ({ name: c.name, type: c.chartType })),
      });
    }

    return {
      ok: true,
      data: {
        sheets: sheetSummaries,
        namedRanges: names.items.map(n => ({ name: n.name, scope: n.scope, comment: n.comment, formula: n.formula, value: n.value })),
      },
    };
  });
}

async function listNamedRanges(): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const names = ctx.workbook.names;
    names.load('items/name,items/comment,items/value,items/formula,items/scope');
    await ctx.sync();
    return {
      ok: true,
      data: {
        namedRanges: names.items.map(n => ({ name: n.name, scope: n.scope, comment: n.comment, formula: n.formula, value: n.value })),
      },
    };
  });
}

async function defineNamedRange(input: { name: string; reference: string; comment?: string }): Promise<ToolResult> {
  if (!input.reference?.startsWith('=')) {
    return { ok: false, error: 'reference must start with "=" (e.g. "=Sheet1!$A$1:$A$10").' };
  }
  return Excel.run(async (ctx) => {
    try {
      ctx.workbook.names.add(input.name, input.reference, input.comment);
      await ctx.sync();
      return { ok: true, data: { name: input.name, reference: input.reference } };
    } catch (e) {
      return { ok: false, error: `Could not define named range: ${(e as Error).message}` };
    }
  });
}

async function listTablesTool(): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const tables = ctx.workbook.tables;
    tables.load('items/name,items/showHeaders,items/showTotals,items/style');
    await ctx.sync();
    const out = [];
    for (const t of tables.items) {
      const range = t.getRange();
      range.load('address');
      const sheet = t.worksheet;
      sheet.load('name');
      await ctx.sync();
      out.push({
        name: t.name,
        sheet: sheet.name,
        address: range.address.split('!').pop() ?? range.address,
        showHeaders: t.showHeaders,
        showTotals: t.showTotals,
        style: t.style,
      });
    }
    return { ok: true, data: { tables: out } };
  });
}

async function createTableTool(input: {
  sheet: string; address: string; hasHeaders?: boolean; name?: string; tableStyle?: string; showTotalsRow?: boolean;
}): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    sheet.load('name');
    await ctx.sync();
    if (sheet.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };
    try {
      const table = sheet.tables.add(input.address, input.hasHeaders ?? true);
      if (input.name) table.name = input.name;
      if (input.tableStyle) table.style = input.tableStyle;
      if (input.showTotalsRow != null) table.showTotals = input.showTotalsRow;
      table.load('name');
      await ctx.sync();
      return { ok: true, data: { sheet: sheet.name, name: table.name, address: input.address } };
    } catch (e) {
      return { ok: false, error: `Could not create table: ${(e as Error).message}` };
    }
  });
}

async function readTableTool(input: { name: string }): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const t = ctx.workbook.tables.getItemOrNullObject(input.name);
    await ctx.sync();
    if (t.isNullObject) return { ok: false, error: `Table not found: ${input.name}` };
    const headerRow = t.getHeaderRowRange();
    const body = t.getDataBodyRange();
    headerRow.load('values');
    body.load(['values', 'address', 'rowCount', 'columnCount']);
    const sheet = t.worksheet; sheet.load('name');
    await ctx.sync();
    if (body.rowCount * body.columnCount > MAX_CELLS_PER_TOOL) {
      return { ok: false, error: `Table data too large (${body.rowCount * body.columnCount} cells).` };
    }
    return {
      ok: true,
      data: {
        name: input.name,
        sheet: sheet.name,
        address: body.address.split('!').pop() ?? body.address,
        headers: headerRow.values[0],
        rows: body.values,
      },
    };
  });
}

// ---------- STRUCTURAL ----------

async function insertRows(input: { sheet: string; rowRange: string }): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    sheet.load('name');
    await ctx.sync();
    if (sheet.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };
    sheet.getRange(input.rowRange).getEntireRow().insert('Down' as Excel.InsertShiftDirection);
    await ctx.sync();
    return { ok: true, data: { sheet: sheet.name, rowRange: input.rowRange } };
  });
}

async function insertColumns(input: { sheet: string; columnRange: string }): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    sheet.load('name');
    await ctx.sync();
    if (sheet.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };
    sheet.getRange(input.columnRange).getEntireColumn().insert('Right' as Excel.InsertShiftDirection);
    await ctx.sync();
    return { ok: true, data: { sheet: sheet.name, columnRange: input.columnRange } };
  });
}

async function deleteRows(input: { sheet: string; rowRange: string }): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    sheet.load('name');
    await ctx.sync();
    if (sheet.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };
    sheet.getRange(input.rowRange).getEntireRow().delete('Up' as Excel.DeleteShiftDirection);
    await ctx.sync();
    return { ok: true, data: { sheet: sheet.name, rowRange: input.rowRange } };
  });
}

async function deleteColumns(input: { sheet: string; columnRange: string }): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    sheet.load('name');
    await ctx.sync();
    if (sheet.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };
    sheet.getRange(input.columnRange).getEntireColumn().delete('Left' as Excel.DeleteShiftDirection);
    await ctx.sync();
    return { ok: true, data: { sheet: sheet.name, columnRange: input.columnRange } };
  });
}

async function clearRange(input: { sheet: string; address: string; applyTo?: string }): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    sheet.load('name');
    await ctx.sync();
    if (sheet.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };
    const map: Record<string, Excel.ClearApplyTo> = {
      all: 'All' as Excel.ClearApplyTo,
      contents: 'Contents' as Excel.ClearApplyTo,
      formats: 'Formats' as Excel.ClearApplyTo,
      comments: 'Comments' as Excel.ClearApplyTo,
      hyperlinks: 'Hyperlinks' as Excel.ClearApplyTo,
    };
    sheet.getRange(input.address).clear(map[input.applyTo ?? 'all'] ?? map.all);
    await ctx.sync();
    return { ok: true, data: { sheet: sheet.name, address: input.address, applyTo: input.applyTo ?? 'all' } };
  });
}

async function copyRange(input: {
  sourceSheet: string; sourceAddress: string;
  destinationSheet: string; destinationAddress: string;
  copyType?: string; skipBlanks?: boolean; transpose?: boolean;
}): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const src = ctx.workbook.worksheets.getItemOrNullObject(input.sourceSheet);
    const dst = ctx.workbook.worksheets.getItemOrNullObject(input.destinationSheet);
    await ctx.sync();
    if (src.isNullObject) return { ok: false, error: `Source sheet not found: ${input.sourceSheet}` };
    if (dst.isNullObject) return { ok: false, error: `Destination sheet not found: ${input.destinationSheet}` };
    const map: Record<string, Excel.RangeCopyType> = {
      all: 'All' as Excel.RangeCopyType,
      values: 'Values' as Excel.RangeCopyType,
      formulas: 'Formulas' as Excel.RangeCopyType,
      formats: 'Formats' as Excel.RangeCopyType,
    };
    const dstRange = dst.getRange(input.destinationAddress);
    dstRange.copyFrom(
      `${input.sourceSheet}!${input.sourceAddress}`,
      map[input.copyType ?? 'all'] ?? map.all,
      input.skipBlanks ?? false,
      input.transpose ?? false
    );
    await ctx.sync();
    return { ok: true, data: { copied: true } };
  });
}

async function findReplace(input: {
  sheet?: string; address?: string; find: string; replace: string; matchCase?: boolean; wholeCell?: boolean;
}): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const sheet = input.sheet
      ? ctx.workbook.worksheets.getItemOrNullObject(input.sheet)
      : ctx.workbook.worksheets.getActiveWorksheet();
    sheet.load('name');
    await ctx.sync();
    if ('isNullObject' in sheet && (sheet as any).isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };
    let range: Excel.Range;
    if (input.address) range = sheet.getRange(input.address);
    else {
      const used = sheet.getUsedRangeOrNullObject();
      await ctx.sync();
      if (used.isNullObject) return { ok: true, data: { replacements: 0 } };
      used.load('address');
      await ctx.sync();
      range = sheet.getRange(used.address.split('!').pop() ?? used.address);
    }
    const replaced = range.replaceAll(input.find, input.replace, {
      completeMatch: input.wholeCell ?? true,
      matchCase: input.matchCase ?? false,
    } as Excel.ReplaceCriteria);
    await ctx.sync();
    return { ok: true, data: { replacements: (replaced as any).value ?? 0 } };
  });
}

// ---------- WRITE ----------

async function createSheet(input: { name: string; replaceIfExists?: boolean }): Promise<ToolResult> {
  if (!input.name || input.name.length > 31) {
    return { ok: false, error: 'Sheet name must be 1–31 characters.' };
  }
  return Excel.run(async (ctx) => {
    const sheets = ctx.workbook.worksheets;
    const existing = sheets.getItemOrNullObject(input.name);
    await ctx.sync();
    let replaced = false;
    if (!existing.isNullObject) {
      if (!input.replaceIfExists) {
        return {
          ok: false,
          error: `Sheet "${input.name}" already exists. Call again with replaceIfExists=true to overwrite, or pick a different name.`,
        };
      }
      existing.delete();
      await ctx.sync();
      replaced = true;
    }
    const created = sheets.add(input.name);
    created.activate();
    await ctx.sync();
    return { ok: true, data: { sheet: input.name, created: true, replaced } };
  });
}

async function writeSheet(input: {
  sheet: string;
  startCell: string;
  values: CellValue[][];
  boldFirstRow?: boolean;
  autoFitColumns?: boolean;
  numberFormats?: { column: number; format: string }[];
}): Promise<ToolResult> {
  if (!input.values?.length) return { ok: false, error: 'values is empty.' };
  const rows = input.values.length;
  const cols = input.values[0]?.length ?? 0;
  if (cols === 0) return { ok: false, error: 'values rows are empty.' };
  if (rows * cols > MAX_WRITE_CELLS) {
    return { ok: false, error: `Write too large: ${rows * cols} cells exceeds limit of ${MAX_WRITE_CELLS}.` };
  }
  for (const row of input.values) {
    if (row.length !== cols) return { ok: false, error: 'All rows in values must have the same length.' };
  }

  return Excel.run(async (ctx) => {
    let sheet = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    await ctx.sync();
    if (sheet.isNullObject) {
      sheet = ctx.workbook.worksheets.add(input.sheet);
      await ctx.sync();
    }

    const start = sheet.getRange(input.startCell);
    const range = start.getResizedRange(rows - 1, cols - 1);
    range.values = input.values as any;

    if (input.boldFirstRow) {
      const header = start.getResizedRange(0, cols - 1);
      header.format.font.bold = true;
    }
    if (input.numberFormats?.length) {
      for (const nf of input.numberFormats) {
        if (nf.column < 0 || nf.column >= cols) continue;
        const col = start.getOffsetRange(0, nf.column).getResizedRange(rows - 1, 0);
        const formatRows: string[][] = [];
        for (let i = 0; i < rows; i++) formatRows.push([nf.format]);
        col.numberFormat = formatRows;
      }
    }
    if (input.autoFitColumns) {
      range.format.autofitColumns();
    }
    range.load('address');
    sheet.load('name');
    sheet.activate();
    await ctx.sync();
    return {
      ok: true,
      data: {
        sheet: sheet.name,
        range: range.address.split('!').pop() ?? range.address,
        rows,
        cols,
      },
    };
  });
}

async function addFormulas(input: {
  sheet: string;
  startCell: string;
  formulas: (string | number | boolean | null)[][];
}): Promise<ToolResult> {
  if (!input.formulas?.length) return { ok: false, error: 'formulas is empty.' };
  const rows = input.formulas.length;
  const cols = input.formulas[0]?.length ?? 0;
  if (cols === 0) return { ok: false, error: 'formulas rows are empty.' };
  for (const row of input.formulas) {
    if (row.length !== cols) return { ok: false, error: 'All formula rows must be the same length.' };
  }
  if (rows * cols > 50_000) {
    return { ok: false, error: `Formula block too large: ${rows * cols} cells (max 50,000).` };
  }

  return Excel.run(async (ctx) => {
    let sheet = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    await ctx.sync();
    if (sheet.isNullObject) {
      sheet = ctx.workbook.worksheets.add(input.sheet);
      await ctx.sync();
    }
    const start = sheet.getRange(input.startCell);
    const range = start.getResizedRange(rows - 1, cols - 1);

    // Normalise: convert to strings, leave nulls as empty
    const grid: string[][] = input.formulas.map(r =>
      r.map(c => c == null ? '' : String(c))
    );
    range.formulas = grid;
    range.load('address');
    sheet.load('name');
    await ctx.sync();
    return {
      ok: true,
      data: {
        sheet: sheet.name,
        range: range.address.split('!').pop() ?? range.address,
        rows, cols,
      },
    };
  });
}

async function addFormula(input: { sheet: string; address: string; formula: string }): Promise<ToolResult> {
  if (!input.formula?.startsWith('=')) {
    return { ok: false, error: 'Formula must start with "=".' };
  }
  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    sheet.load('name');
    await ctx.sync();
    if (sheet.isNullObject) {
      return { ok: false, error: `Sheet not found: ${input.sheet}` };
    }
    try {
      const range = sheet.getRange(input.address);
      range.load(['address', 'rowCount', 'columnCount']);
      await ctx.sync();
      const formulas: string[][] = [];
      for (let i = 0; i < range.rowCount; i++) {
        formulas.push(new Array(range.columnCount).fill(input.formula));
      }
      range.formulas = formulas;
      await ctx.sync();
      return {
        ok: true,
        data: {
          sheet: sheet.name,
          address: range.address.split('!').pop() ?? range.address,
          formula: input.formula,
        },
      };
    } catch (e) {
      return { ok: false, error: `Could not apply formula: ${(e as Error).message}` };
    }
  });
}

// ---------- FORMATTING ----------

const BORDER_STYLE_MAP: Record<string, Excel.BorderLineStyle> = {
  continuous: 'Continuous' as Excel.BorderLineStyle,
  dash: 'Dash' as Excel.BorderLineStyle,
  double: 'Double' as Excel.BorderLineStyle,
};
const BORDER_WEIGHT_MAP: Record<string, Excel.BorderWeight> = {
  thin: 'Thin' as Excel.BorderWeight,
  medium: 'Medium' as Excel.BorderWeight,
  thick: 'Thick' as Excel.BorderWeight,
};
const HALIGN_MAP: Record<string, Excel.HorizontalAlignment> = {
  left: 'Left' as Excel.HorizontalAlignment,
  center: 'Center' as Excel.HorizontalAlignment,
  right: 'Right' as Excel.HorizontalAlignment,
  justify: 'Justify' as Excel.HorizontalAlignment,
};
const VALIGN_MAP: Record<string, Excel.VerticalAlignment> = {
  top: 'Top' as Excel.VerticalAlignment,
  middle: 'Center' as Excel.VerticalAlignment,
  bottom: 'Bottom' as Excel.VerticalAlignment,
};

async function formatRange(input: {
  sheet: string;
  address: string;
  font?: { bold?: boolean; italic?: boolean; size?: number; color?: string; name?: string };
  fill?: { color?: string };
  alignment?: { horizontal?: string; vertical?: string; wrapText?: boolean };
  borders?: { edges: string[]; style?: string; color?: string; weight?: string };
  numberFormat?: string;
  merge?: boolean;
}): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    sheet.load('name');
    await ctx.sync();
    if (sheet.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };

    const range = sheet.getRange(input.address);

    if (input.font) {
      if (input.font.bold != null) range.format.font.bold = input.font.bold;
      if (input.font.italic != null) range.format.font.italic = input.font.italic;
      if (input.font.size != null) range.format.font.size = input.font.size;
      if (input.font.color) range.format.font.color = input.font.color;
      if (input.font.name) range.format.font.name = input.font.name;
    }
    if (input.fill?.color) {
      range.format.fill.color = input.fill.color;
    }
    if (input.alignment) {
      if (input.alignment.horizontal) range.format.horizontalAlignment = HALIGN_MAP[input.alignment.horizontal];
      if (input.alignment.vertical) range.format.verticalAlignment = VALIGN_MAP[input.alignment.vertical];
      if (input.alignment.wrapText != null) range.format.wrapText = input.alignment.wrapText;
    }
    if (input.numberFormat) {
      range.load(['rowCount', 'columnCount']);
      await ctx.sync();
      const fmt: string[][] = [];
      for (let i = 0; i < range.rowCount; i++) {
        fmt.push(new Array(range.columnCount).fill(input.numberFormat));
      }
      range.numberFormat = fmt;
    }
    if (input.borders?.edges?.length) {
      const style = BORDER_STYLE_MAP[input.borders.style ?? 'continuous'];
      const weight = BORDER_WEIGHT_MAP[input.borders.weight ?? 'thin'];
      const color = input.borders.color ?? '#000000';
      const edgeMap: Record<string, Excel.BorderIndex[]> = {
        top: ['EdgeTop' as Excel.BorderIndex],
        bottom: ['EdgeBottom' as Excel.BorderIndex],
        left: ['EdgeLeft' as Excel.BorderIndex],
        right: ['EdgeRight' as Excel.BorderIndex],
        all: ['EdgeTop','EdgeBottom','EdgeLeft','EdgeRight'] as Excel.BorderIndex[],
        inside: ['InsideHorizontal','InsideVertical'] as Excel.BorderIndex[],
      };
      const borderIndices = new Set<Excel.BorderIndex>();
      for (const edge of input.borders.edges) {
        for (const idx of (edgeMap[edge] ?? [])) borderIndices.add(idx);
      }
      for (const idx of borderIndices) {
        const b = range.format.borders.getItem(idx);
        b.style = style;
        b.weight = weight;
        b.color = color;
      }
    }
    if (input.merge) {
      range.merge(true);
    }
    range.load('address');
    await ctx.sync();
    return {
      ok: true,
      data: { sheet: sheet.name, address: range.address.split('!').pop() ?? range.address, applied: true },
    };
  });
}

async function freezePanes(input: { sheet: string; rows?: number; columns?: number }): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    sheet.load('name');
    await ctx.sync();
    if (sheet.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };
    const rows = input.rows ?? 0;
    const cols = input.columns ?? 0;
    sheet.freezePanes.unfreeze();
    if (rows > 0) sheet.freezePanes.freezeRows(rows);
    if (cols > 0) sheet.freezePanes.freezeColumns(cols);
    await ctx.sync();
    return { ok: true, data: { sheet: sheet.name, rows, columns: cols } };
  });
}

async function setColumnWidths(input: {
  sheet: string;
  widths: { columnAddress: string; width: number }[];
}): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    sheet.load('name');
    await ctx.sync();
    if (sheet.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };
    for (const w of input.widths ?? []) {
      sheet.getRange(w.columnAddress).format.columnWidth = w.width;
    }
    await ctx.sync();
    return { ok: true, data: { sheet: sheet.name, applied: (input.widths ?? []).length } };
  });
}

// ---------- ADVANCED FORMATTING ----------

async function conditionalFormat(input: any): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    sheet.load('name');
    await ctx.sync();
    if (sheet.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };
    const range = sheet.getRange(input.address);
    try {
      const typeMap: Record<string, Excel.ConditionalFormatType> = {
        colorScale: 'ColorScale' as Excel.ConditionalFormatType,
        dataBar: 'DataBar' as Excel.ConditionalFormatType,
        iconSet: 'IconSet' as Excel.ConditionalFormatType,
        cellValue: 'CellValue' as Excel.ConditionalFormatType,
        topBottom: 'TopBottom' as Excel.ConditionalFormatType,
      };
      const cf = range.conditionalFormats.add(typeMap[input.type]);

      if (input.type === 'colorScale') {
        const minColor = input.colorScale?.minColor ?? '#F8696B';
        const midColor = input.colorScale?.midColor ?? '#FFEB84';
        const maxColor = input.colorScale?.maxColor ?? '#63BE7B';
        cf.colorScale!.criteria = {
          minimum: { type: 'LowestValue' as Excel.ConditionalFormatColorCriterionType, color: minColor },
          midpoint: { type: 'Percentile' as Excel.ConditionalFormatColorCriterionType, formula: '50', color: midColor },
          maximum: { type: 'HighestValue' as Excel.ConditionalFormatColorCriterionType, color: maxColor },
        };
      } else if (input.type === 'dataBar') {
        cf.dataBar!.positiveFormat.fillColor = input.dataBar?.color ?? '#5B9BD5';
      } else if (input.type === 'iconSet') {
        const styleMap: Record<string, Excel.IconSet> = {
          ThreeArrows: 'ThreeArrows' as Excel.IconSet,
          ThreeTrafficLights1: 'ThreeTrafficLights1' as Excel.IconSet,
          ThreeSymbols2: 'ThreeSymbols2' as Excel.IconSet,
          ThreeFlags: 'ThreeFlags' as Excel.IconSet,
          FiveArrows: 'FiveArrows' as Excel.IconSet,
          FourArrows: 'FourArrows' as Excel.IconSet,
        };
        cf.iconSet!.style = styleMap[input.iconSet?.style ?? 'ThreeArrows'];
        if (input.iconSet?.reverse) cf.iconSet!.reverseIconOrder = true;
      } else if (input.type === 'cellValue') {
        const opMap: Record<string, Excel.ConditionalCellValueOperator> = {
          greaterThan: 'GreaterThan' as Excel.ConditionalCellValueOperator,
          lessThan: 'LessThan' as Excel.ConditionalCellValueOperator,
          greaterThanOrEqual: 'GreaterThanOrEqual' as Excel.ConditionalCellValueOperator,
          lessThanOrEqual: 'LessThanOrEqual' as Excel.ConditionalCellValueOperator,
          equalTo: 'EqualTo' as Excel.ConditionalCellValueOperator,
          notEqualTo: 'NotEqualTo' as Excel.ConditionalCellValueOperator,
          between: 'Between' as Excel.ConditionalCellValueOperator,
          notBetween: 'NotBetween' as Excel.ConditionalCellValueOperator,
        };
        const v1 = String(input.cellValue.value1 ?? '0');
        const v2 = String(input.cellValue.value2 ?? '0');
        cf.cellValue!.rule = { operator: opMap[input.cellValue.operator], formula1: v1, formula2: v2 };
        if (input.cellValue.font?.color) cf.cellValue!.format.font.color = input.cellValue.font.color;
        if (input.cellValue.font?.bold != null) cf.cellValue!.format.font.bold = input.cellValue.font.bold;
        if (input.cellValue.fill?.color) cf.cellValue!.format.fill.color = input.cellValue.fill.color;
      } else if (input.type === 'topBottom') {
        const ruleMap: Record<string, Excel.ConditionalTopBottomCriterionType> = {
          topItems: 'TopItems' as Excel.ConditionalTopBottomCriterionType,
          bottomItems: 'BottomItems' as Excel.ConditionalTopBottomCriterionType,
          topPercent: 'TopPercent' as Excel.ConditionalTopBottomCriterionType,
          bottomPercent: 'BottomPercent' as Excel.ConditionalTopBottomCriterionType,
        };
        cf.topBottom!.rule = { type: ruleMap[input.topBottom.rule], rank: input.topBottom.rank };
        if (input.topBottom.font?.color) cf.topBottom!.format.font.color = input.topBottom.font.color;
        if (input.topBottom.font?.bold != null) cf.topBottom!.format.font.bold = input.topBottom.font.bold;
        if (input.topBottom.fill?.color) cf.topBottom!.format.fill.color = input.topBottom.fill.color;
      }
      await ctx.sync();
      return { ok: true, data: { sheet: sheet.name, address: input.address, type: input.type } };
    } catch (e) {
      return { ok: false, error: `Conditional format failed: ${(e as Error).message}` };
    }
  });
}

async function addChart(input: {
  sheet: string; type: string; sourceRange: string; title?: string;
  anchorCell?: string; widthCells?: number; heightCells?: number; showLegend?: boolean;
}): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    sheet.load('name');
    await ctx.sync();
    if (sheet.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };
    const typeMap: Record<string, Excel.ChartType> = {
      column: 'ColumnClustered' as Excel.ChartType,
      columnStacked: 'ColumnStacked' as Excel.ChartType,
      bar: 'BarClustered' as Excel.ChartType,
      barStacked: 'BarStacked' as Excel.ChartType,
      line: 'Line' as Excel.ChartType,
      lineMarkers: 'LineMarkers' as Excel.ChartType,
      pie: 'Pie' as Excel.ChartType,
      doughnut: 'Doughnut' as Excel.ChartType,
      area: 'Area' as Excel.ChartType,
      areaStacked: 'AreaStacked' as Excel.ChartType,
      combo: 'ColumnClustered' as Excel.ChartType, // combo is built afterwards
      waterfall: 'Waterfall' as Excel.ChartType,
      scatter: 'XYScatter' as Excel.ChartType,
    };
    try {
      // sourceRange may include sheet name; if not, scope to this sheet
      const sourceAddr = input.sourceRange.includes('!')
        ? input.sourceRange
        : `${sheet.name}!${input.sourceRange}`;
      const dataRange = ctx.workbook.worksheets.getItem(sourceAddr.split('!')[0].replace(/^'|'$/g, '')).getRange(sourceAddr.split('!').pop()!);
      const chart = sheet.charts.add(typeMap[input.type] ?? typeMap.column, dataRange, 'Auto' as Excel.ChartSeriesBy);
      if (input.title) {
        chart.title.text = input.title;
        chart.title.visible = true;
      }
      if (input.showLegend != null) chart.legend.visible = input.showLegend;
      // Anchor & sizing — set top/left to anchor cell, then width/height in points
      if (input.anchorCell) {
        const anchor = sheet.getRange(input.anchorCell);
        anchor.load(['top', 'left']);
        await ctx.sync();
        chart.top = anchor.top;
        chart.left = anchor.left;
      }
      const width = (input.widthCells ?? 8) * 64;   // ~64 pt per column unit (rough)
      const height = (input.heightCells ?? 14) * 15; // ~15 pt per row unit
      chart.width = width;
      chart.height = height;
      chart.load('name');
      await ctx.sync();
      return { ok: true, data: { sheet: sheet.name, name: chart.name, type: input.type } };
    } catch (e) {
      return { ok: false, error: `Chart insert failed: ${(e as Error).message}` };
    }
  });
}

async function addDataValidation(input: any): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    sheet.load('name');
    await ctx.sync();
    if (sheet.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };
    const range = sheet.getRange(input.address);
    try {
      let rule: Excel.DataValidationRule;
      if (input.type === 'list') {
        rule = { list: { source: (input.list?.values ?? []).join(','), inCellDropDown: true } };
      } else if (input.type === 'wholeNumber' || input.type === 'decimal') {
        const opMap: Record<string, Excel.DataValidationOperator> = {
          between: 'Between' as Excel.DataValidationOperator,
          notBetween: 'NotBetween' as Excel.DataValidationOperator,
          greaterThan: 'GreaterThan' as Excel.DataValidationOperator,
          lessThan: 'LessThan' as Excel.DataValidationOperator,
          greaterThanOrEqual: 'GreaterThanOrEqual' as Excel.DataValidationOperator,
          lessThanOrEqual: 'LessThanOrEqual' as Excel.DataValidationOperator,
          equalTo: 'EqualTo' as Excel.DataValidationOperator,
          notEqualTo: 'NotEqualTo' as Excel.DataValidationOperator,
        };
        const op = opMap[input.numberRange?.operator ?? 'between'];
        const f1 = String(input.numberRange?.min ?? 0);
        const f2 = String(input.numberRange?.max ?? 100);
        if (input.type === 'wholeNumber') rule = { wholeNumber: { operator: op, formula1: f1, formula2: f2 } };
        else rule = { decimal: { operator: op, formula1: f1, formula2: f2 } };
      } else if (input.type === 'custom') {
        rule = { custom: { formula: input.custom?.formula ?? '=TRUE' } };
      } else if (input.type === 'date') {
        rule = { date: { operator: 'GreaterThan' as Excel.DataValidationOperator, formula1: '1900-01-01' } };
      } else if (input.type === 'textLength') {
        rule = { textLength: { operator: 'GreaterThan' as Excel.DataValidationOperator, formula1: '0' } };
      } else {
        return { ok: false, error: `Unsupported validation type: ${input.type}` };
      }
      range.dataValidation.rule = rule;
      if (input.prompt) {
        range.dataValidation.prompt = {
          showPrompt: true,
          title: input.prompt.title ?? '',
          message: input.prompt.message ?? '',
        };
      }
      if (input.errorAlert) {
        const styleMap: Record<string, Excel.DataValidationAlertStyle> = {
          stop: 'Stop' as Excel.DataValidationAlertStyle,
          warning: 'Warning' as Excel.DataValidationAlertStyle,
          information: 'Information' as Excel.DataValidationAlertStyle,
        };
        range.dataValidation.errorAlert = {
          showAlert: true,
          style: styleMap[input.errorAlert.style ?? 'stop'],
          title: input.errorAlert.title ?? '',
          message: input.errorAlert.message ?? '',
        };
      }
      await ctx.sync();
      return { ok: true, data: { sheet: sheet.name, address: input.address, type: input.type } };
    } catch (e) {
      return { ok: false, error: `Data validation failed: ${(e as Error).message}` };
    }
  });
}

// ---------- WORKBOOK / SHEET MANAGEMENT ----------

async function setCalculationMode(input: { mode: string }): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const map: Record<string, Excel.CalculationMode> = {
      Automatic: 'Automatic' as Excel.CalculationMode,
      Manual: 'Manual' as Excel.CalculationMode,
      AutomaticExceptTables: 'AutomaticExceptTables' as Excel.CalculationMode,
    };
    ctx.application.calculationMode = map[input.mode] ?? map.Automatic;
    await ctx.sync();
    return { ok: true, data: { mode: input.mode } };
  });
}

async function setSheetVisibility(input: { sheet: string; visibility: string }): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const s = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    s.load('name');
    await ctx.sync();
    if (s.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };
    const map: Record<string, Excel.SheetVisibility> = {
      Visible: 'Visible' as Excel.SheetVisibility,
      Hidden: 'Hidden' as Excel.SheetVisibility,
      VeryHidden: 'VeryHidden' as Excel.SheetVisibility,
    };
    s.visibility = map[input.visibility] ?? map.Visible;
    await ctx.sync();
    return { ok: true, data: { sheet: s.name, visibility: input.visibility } };
  });
}

async function setSheetTabColor(input: { sheet: string; color: string }): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const s = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    s.load('name');
    await ctx.sync();
    if (s.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };
    s.tabColor = input.color;
    await ctx.sync();
    return { ok: true, data: { sheet: s.name, color: input.color } };
  });
}

async function protectSheet(input: { sheet: string; password?: string }): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const s = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    s.load('name');
    await ctx.sync();
    if (s.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };
    s.protection.protect({}, input.password);
    await ctx.sync();
    return { ok: true, data: { sheet: s.name, protected: true } };
  });
}

async function unprotectSheet(input: { sheet: string; password?: string }): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const s = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    s.load('name');
    await ctx.sync();
    if (s.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };
    s.protection.unprotect(input.password);
    await ctx.sync();
    return { ok: true, data: { sheet: s.name, protected: false } };
  });
}

async function markInputCells(input: { sheet: string; address: string; applyInputFill?: boolean }): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const s = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    s.load('name');
    await ctx.sync();
    if (s.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };
    const range = s.getRange(input.address);
    range.format.protection.locked = false;
    if (input.applyInputFill !== false) range.format.fill.color = '#FFF2CC';
    await ctx.sync();
    return { ok: true, data: { sheet: s.name, address: input.address, unlocked: true } };
  });
}

async function hideRowsColumns(input: { sheet: string; rowRange?: string; columnRange?: string; hidden: boolean }): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const s = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    s.load('name');
    await ctx.sync();
    if (s.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };
    if (input.rowRange) {
      s.getRange(input.rowRange).rowHidden = input.hidden;
    } else if (input.columnRange) {
      s.getRange(input.columnRange).columnHidden = input.hidden;
    } else {
      return { ok: false, error: 'Provide rowRange or columnRange.' };
    }
    await ctx.sync();
    return { ok: true, data: { sheet: s.name, hidden: input.hidden } };
  });
}

async function addCommentTool(input: { sheet: string; cell: string; text: string }): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const s = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    s.load('name');
    await ctx.sync();
    if (s.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };
    try {
      ctx.workbook.comments.add(`${s.name}!${input.cell}`, input.text);
      await ctx.sync();
      return { ok: true, data: { sheet: s.name, cell: input.cell } };
    } catch (e) {
      return { ok: false, error: `Comment add failed: ${(e as Error).message}` };
    }
  });
}

async function sortRange(input: { sheet: string; address: string; hasHeaders?: boolean; orderBy: { column: number; ascending: boolean }[] }): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const s = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    s.load('name');
    await ctx.sync();
    if (s.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };
    const range = s.getRange(input.address);
    const fields: Excel.SortField[] = input.orderBy.map(o => ({
      key: o.column,
      ascending: o.ascending,
    }));
    range.sort.apply(fields, false, input.hasHeaders ?? true);
    await ctx.sync();
    return { ok: true, data: { sheet: s.name, address: input.address, sorted: true } };
  });
}

async function removeDuplicates(input: { sheet: string; address: string; keyColumns: number[]; hasHeaders?: boolean }): Promise<ToolResult> {
  return Excel.run(async (ctx) => {
    const s = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    s.load('name');
    await ctx.sync();
    if (s.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };
    const range = s.getRange(input.address);
    const result = range.removeDuplicates(input.keyColumns, input.hasHeaders ?? true);
    result.load(['removed', 'uniqueRemaining']);
    await ctx.sync();
    return { ok: true, data: { sheet: s.name, removed: result.removed, uniqueRemaining: result.uniqueRemaining } };
  });
}

// ---------- COMPLIANCE ----------

async function listStandardsTool(): Promise<ToolResult> {
  return { ok: true, data: { standards: AASB_REGISTRY } };
}

// ---------- DOCUMENT GENERATION ----------

async function exportToPdf(input: { spec: DocSpec; filename?: string }): Promise<ToolResult> {
  if (!input.spec || typeof input.spec !== 'object') return { ok: false, error: 'spec is required.' };
  try {
    const blob = renderPdf(input.spec);
    const filename = (input.filename || sanitiseFilename(input.spec.title || 'practice-iq-report')) + '.pdf';
    downloadBlob(blob, filename);
    return {
      ok: true,
      data: { filename, sizeBytes: blob.size, message: 'PDF generated and download started in the user\'s browser.' },
    };
  } catch (e) {
    return { ok: false, error: `PDF generation failed: ${(e as Error).message}` };
  }
}

async function generateWordMemo(input: { spec: DocSpec; filename?: string }): Promise<ToolResult> {
  if (!input.spec || typeof input.spec !== 'object') return { ok: false, error: 'spec is required.' };
  try {
    const blob = await generateWordDocx(input.spec);
    const filename = (input.filename || sanitiseFilename(input.spec.title || 'practice-iq-memo')) + '.docx';
    downloadBlob(blob, filename);
    return { ok: true, data: { filename, sizeBytes: blob.size, message: 'Word memo generated and download started.' } };
  } catch (e) {
    return { ok: false, error: `Word generation failed: ${(e as Error).message}` };
  }
}

// ---------- FINANCIAL MATH (Rounds 12–14) ----------

async function calculateLandedCostTool(input: any): Promise<ToolResult> {
  try {
    const r = calculateLandedCost(input);
    const data: any = {
      cifForeign: +r.cifForeign.toFixed(2),
      cifAud: +r.cifAud.toFixed(2),
      customsDuty: +r.customsDuty.toFixed(2),
      gst: +r.gst.toFixed(2),
      totalAud: +r.totalAud.toFixed(2),
      perUnitAud: +r.perUnitAud.toFixed(2),
    };
    if (input.sellPriceAud) {
      const m = r.marginAtSellPrice!(input.sellPriceAud);
      data.grossProfitAud = +m.gpDollar.toFixed(2);
      data.grossMarginPct = +(m.gpPct * 100).toFixed(2);
      data.breakEvenFx = +r.breakEvenFx!(input.sellPriceAud).toFixed(4);
    }
    return { ok: true, data };
  } catch (e) { return { ok: false, error: (e as Error).message }; }
}

async function applyCpiEscalationTool(input: { baseValue: number; annualCpi: number[] }): Promise<ToolResult> {
  try {
    const series = applyCpiEscalation(input.baseValue, input.annualCpi);
    return { ok: true, data: { baseValue: input.baseValue, annualCpi: input.annualCpi, escalatedSeries: series } };
  } catch (e) { return { ok: false, error: (e as Error).message }; }
}

async function nominalToRealTool(input: { values: number[]; cpiIndex: number[]; baseIndex?: number }): Promise<ToolResult> {
  try {
    const real = nominalToReal(input.values, input.cpiIndex, input.baseIndex);
    return { ok: true, data: { nominalSeries: input.values, cpiIndex: input.cpiIndex, realSeries: real.map(v => +v.toFixed(2)) } };
  } catch (e) { return { ok: false, error: (e as Error).message }; }
}

async function bondValueTool(input: any): Promise<ToolResult> {
  try {
    const price = bondPrice(input);
    const md = modifiedDuration(input);
    const mac = macaulayDuration(input);
    return { ok: true, data: { cleanPrice: +price.toFixed(2), macaulayDuration: +mac.toFixed(3), modifiedDuration: +md.toFixed(3) } };
  } catch (e) { return { ok: false, error: (e as Error).message }; }
}

async function yieldToMaturityTool(input: any): Promise<ToolResult> {
  try {
    const ytm = yieldToMaturity(input.price, input.faceValue, input.couponRate, input.paymentsPerYear, input.yearsToMaturity);
    return { ok: true, data: { ytm, ytmPct: +(ytm * 100).toFixed(4) } };
  } catch (e) { return { ok: false, error: (e as Error).message }; }
}

async function testCovenantsTool(input: any): Promise<ToolResult> {
  try {
    const tests = testCovenants(input, input.thresholds);
    const breachCount = tests.filter(t => t.breach).length;
    return { ok: true, data: { tests, breachCount, allCompliant: breachCount === 0 } };
  } catch (e) { return { ok: false, error: (e as Error).message }; }
}

async function investmentAnalysisTool(input: { cashflows: number[]; hurdleRate: number }): Promise<ToolResult> {
  try {
    const npvVal = npv(input.hurdleRate, input.cashflows);
    const irrVal = irr(input.cashflows);
    const pb = payback(input.cashflows);
    return {
      ok: true,
      data: {
        npv: +npvVal.toFixed(2),
        npvPositive: npvVal > 0,
        irr: irrVal,
        irrPct: irrVal != null ? +(irrVal * 100).toFixed(4) : null,
        paybackPeriod: pb != null ? +pb.toFixed(2) : null,
      },
    };
  } catch (e) { return { ok: false, error: (e as Error).message }; }
}

async function disclosureChecklistTool(input: ChecklistContext): Promise<ToolResult> {
  try {
    const items = generateDisclosureChecklist(input);
    const byCategory: Record<string, typeof items> = {};
    for (const i of items) (byCategory[i.category] ??= []).push(i);
    return {
      ok: true,
      data: { profile: input.profile, totalItems: items.length, byCategory, items },
    };
  } catch (e) {
    return { ok: false, error: `Checklist failed: ${(e as Error).message}` };
  }
}

async function generatePowerPointDeck(input: { spec: DocSpec; filename?: string }): Promise<ToolResult> {
  if (!input.spec || typeof input.spec !== 'object') return { ok: false, error: 'spec is required.' };
  try {
    const blob = await generatePowerPoint(input.spec);
    const filename = (input.filename || sanitiseFilename(input.spec.title || 'practice-iq-deck')) + '.pptx';
    downloadBlob(blob, filename);
    return { ok: true, data: { filename, sizeBytes: blob.size, message: 'PowerPoint deck generated and download started.' } };
  } catch (e) {
    return { ok: false, error: `PowerPoint generation failed: ${(e as Error).message}` };
  }
}

function sanitiseFilename(s: string): string {
  return s.replace(/[^A-Za-z0-9_\- ]+/g, '').replace(/\s+/g, '-').slice(0, 80) || 'document';
}


async function readHeaderAndData(
  sheetName: string | undefined,
  headerAddr: string | undefined,
  dataAddr: string | undefined
): Promise<{ ok: true; sheet: string; header: string[]; data: CellValue[][] } | { ok: false; error: string }> {
  return Excel.run(async (ctx) => {
    const sheet = sheetName
      ? ctx.workbook.worksheets.getItemOrNullObject(sheetName)
      : ctx.workbook.worksheets.getActiveWorksheet();
    sheet.load('name');
    await ctx.sync();
    if ('isNullObject' in sheet && (sheet as any).isNullObject) {
      return { ok: false as const, error: `Sheet not found: ${sheetName}` };
    }

    let headerValues: CellValue[][];
    let dataValues: CellValue[][];

    if (headerAddr && dataAddr) {
      const headerRange = sheet.getRange(headerAddr);
      const dataRange = sheet.getRange(dataAddr);
      headerRange.load('values');
      dataRange.load('values');
      await ctx.sync();
      headerValues = headerRange.values as CellValue[][];
      dataValues = dataRange.values as CellValue[][];
    } else {
      const used = sheet.getUsedRangeOrNullObject();
      used.load(['values', 'rowCount']);
      await ctx.sync();
      if (used.isNullObject || used.rowCount === 0) {
        return { ok: false as const, error: `Sheet "${sheet.name}" is empty.` };
      }
      const all = used.values as CellValue[][];
      headerValues = [all[0]];
      dataValues = all.slice(1);
    }

    const header = headerValues[0]?.map((v) => String(v ?? '')) ?? [];
    return { ok: true as const, sheet: sheet.name, header, data: dataValues };
  });
}

// Lightweight header fingerprints for other common schemas — used to suggest
// a likely AI-interpretive validation when no rulepack exists.
const SCHEMA_FINGERPRINTS: Array<{ standard: string; keywords: string[]; minHits: number }> = [
  { standard: 'AASB_116', keywords: ['asset_id', 'cost', 'useful_life', 'depreciation', 'carrying_value', 'residual', 'accumulated_depreciation'], minHits: 3 },
  { standard: 'AASB_16',  keywords: ['lease_id', 'lessor', 'commencement_date', 'lease_term', 'lease_payment', 'right_of_use', 'lease_liability', 'discount_rate'], minHits: 3 },
  { standard: 'AASB_15',  keywords: ['contract_id', 'customer', 'revenue_type', 'recognition', 'contract_value', 'performance_obligation', 'recognised', 'po'], minHits: 3 },
  { standard: 'AASB_137', keywords: ['provision', 'onerous', 'restructuring', 'warranty', 'settlement_date', 'best_estimate', 'recognition_criteria'], minHits: 2 },
  { standard: 'AASB_136', keywords: ['cgu', 'recoverable_amount', 'carrying_amount', 'value_in_use', 'fair_value', 'impairment', 'goodwill'], minHits: 2 },
  { standard: 'AASB_102', keywords: ['inventory', 'cost', 'nrv', 'quantity', 'stock'], minHits: 2 },
  { standard: 'AASB_138', keywords: ['intangible', 'amortisation', 'useful_life'], minHits: 2 },
  { standard: 'AASB_140', keywords: ['investment_property', 'fair_value', 'carrying_amount'], minHits: 2 },
];

function tokenise(header: string[]): string[] {
  return header.map(h => String(h ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''));
}

async function detectStandardTool(input: { sheet?: string; headerRowAddress?: string }): Promise<ToolResult> {
  const read = await readHeaderAndData(input.sheet, input.headerRowAddress, input.headerRowAddress);
  if (!read.ok) return { ok: false, error: read.error };

  const normalized = tokenise(read.header);

  // Rulepack detections
  const aasb116 = detectAASB116(read.header);
  const aasb16 = detectAASB16(read.header);
  const aasb15 = detectAASB15(read.header);
  const aasb137 = detectAASB137(read.header);
  const aasb102 = detectAASB102(read.header);
  const aasb119 = detectAASB119(read.header);
  const aasb138 = detectAASB138(read.header);
  const aasb136 = detectAASB136(read.header);
  const aasb124 = detectAASB124(read.header);
  const aasb133 = detectAASB133(read.header);
  const aasb8 = detectAASB8(read.header);
  const aasb9 = detectAASB9(read.header);
  const aasb112 = detectAASB112(read.header);
  const aasb107 = detectAASB107(read.header);
  const aasb121 = detectAASB121(read.header);
  const aasb123 = detectAASB123(read.header);
  const aasb1058 = detectAASB1058(read.header);

  // Heuristic fingerprints for other standards
  const RULEPACK_CODES = ['AASB_116','AASB_16','AASB_15','AASB_137','AASB_102','AASB_119','AASB_138','AASB_136','AASB_124','AASB_133','AASB_8','AASB_9','AASB_112','AASB_107','AASB_121','AASB_123','AASB_1058'];
  const heuristic = SCHEMA_FINGERPRINTS
    .filter(f => !RULEPACK_CODES.includes(f.standard))
    .map(f => {
      const hits = f.keywords.filter(k => normalized.some(h => h.includes(k))).length;
      const confidence = Math.min(1, hits / Math.max(f.keywords.length, 4));
      return { standard: f.standard, confidence, matched: f.keywords.filter(k => normalized.some(h => h.includes(k))), missing: f.keywords.filter(k => !normalized.some(h => h.includes(k))), mode: 'ai_interpretive' as const };
    })
    .filter(c => c.confidence > 0);

  const candidates = [
    { standard: 'AASB_116', confidence: aasb116.confidence, matched: aasb116.matched, missing: aasb116.missing, mode: 'rulepack' as const },
    { standard: 'AASB_16',  confidence: aasb16.confidence,  matched: aasb16.matched,  missing: aasb16.missing,  mode: 'rulepack' as const },
    { standard: 'AASB_15',  confidence: aasb15.confidence,  matched: aasb15.matched,  missing: aasb15.missing,  mode: 'rulepack' as const },
    { standard: 'AASB_137', confidence: aasb137.confidence, matched: aasb137.matched, missing: aasb137.missing, mode: 'rulepack' as const },
    { standard: 'AASB_102', confidence: aasb102.confidence, matched: aasb102.matched, missing: aasb102.missing, mode: 'rulepack' as const },
    { standard: 'AASB_119', confidence: aasb119.confidence, matched: aasb119.matched, missing: aasb119.missing, mode: 'rulepack' as const },
    { standard: 'AASB_138', confidence: aasb138.confidence, matched: aasb138.matched, missing: aasb138.missing, mode: 'rulepack' as const },
    { standard: 'AASB_136', confidence: aasb136.confidence, matched: aasb136.matched, missing: aasb136.missing, mode: 'rulepack' as const },
    { standard: 'AASB_124', confidence: aasb124.confidence, matched: aasb124.matched, missing: aasb124.missing, mode: 'rulepack' as const },
    { standard: 'AASB_133', confidence: aasb133.confidence, matched: aasb133.matched, missing: aasb133.missing, mode: 'rulepack' as const },
    { standard: 'AASB_8',   confidence: aasb8.confidence,   matched: aasb8.matched,   missing: aasb8.missing,   mode: 'rulepack' as const },
    { standard: 'AASB_9',   confidence: aasb9.confidence,   matched: aasb9.matched,   missing: aasb9.missing,   mode: 'rulepack' as const },
    { standard: 'AASB_112', confidence: aasb112.confidence, matched: aasb112.matched, missing: aasb112.missing, mode: 'rulepack' as const },
    { standard: 'AASB_107', confidence: aasb107.confidence, matched: aasb107.matched, missing: aasb107.missing, mode: 'rulepack' as const },
    { standard: 'AASB_121', confidence: aasb121.confidence, matched: aasb121.matched, missing: aasb121.missing, mode: 'rulepack' as const },
    { standard: 'AASB_123', confidence: aasb123.confidence, matched: aasb123.matched, missing: aasb123.missing, mode: 'rulepack' as const },
    { standard: 'AASB_1058', confidence: aasb1058.confidence, matched: aasb1058.matched, missing: aasb1058.missing, mode: 'rulepack' as const },
    ...heuristic,
  ].sort((a, b) => b.confidence - a.confidence);

  const best = candidates[0];
  return {
    ok: true,
    data: {
      sheet: read.sheet,
      bestGuess: best.confidence >= 0.5 ? best.standard : null,
      bestGuessMode: best.confidence >= 0.5 ? best.mode : null,
      confidence: best.confidence,
      candidates,
      headerRowSample: read.header,
      note: best.confidence >= 0.5
        ? `Best match: ${best.standard} (${best.mode}).`
        : 'No strong schema match — ask the user to confirm the standard, or treat this as a general analysis.',
    },
  };
}

async function validateStandardTool(input: {
  standard: string;
  sheet?: string;
  headerRowAddress?: string;
  dataRangeAddress?: string;
  reportDate?: string;
}): Promise<ToolResult> {
  const RULEPACK_STANDARDS = new Set(['AASB_116','AASB_16','AASB_15','AASB_137','AASB_102','AASB_119','AASB_138','AASB_136','AASB_124','AASB_133','AASB_8','AASB_9','AASB_112','AASB_107','AASB_121','AASB_123','AASB_1058']);
  if (!RULEPACK_STANDARDS.has(input.standard)) {
    return {
      ok: true,
      data: {
        standard: input.standard,
        status: 'ai_interpretive_only',
        message: `${input.standard} is in the AASB registry but does not yet have a deterministic rulepack. Approach via AI-interpretive validation: read the data, reason from training on the standard, label findings as "ai_interpretive", cite paragraphs, include the reviewer caveat. Rulepacks shipped: ${[...RULEPACK_STANDARDS].sort().join(', ')}.`,
      },
    };
  }

  const read = await readHeaderAndData(input.sheet, input.headerRowAddress, input.dataRangeAddress);
  if (!read.ok) return { ok: false, error: read.error };

  const reportDate = input.reportDate ? new Date(input.reportDate) : new Date();
  const safeDate = isNaN(+reportDate) ? new Date() : reportDate;
  let result;
  switch (input.standard) {
    case 'AASB_116': result = validateAASB116(read.header, read.data, safeDate); break;
    case 'AASB_16':  result = validateAASB16(read.header, read.data, safeDate); break;
    case 'AASB_15':  result = validateAASB15(read.header, read.data, safeDate); break;
    case 'AASB_137': result = validateAASB137(read.header, read.data, safeDate); break;
    case 'AASB_102': result = validateAASB102(read.header, read.data, safeDate); break;
    case 'AASB_119': result = validateAASB119(read.header, read.data, safeDate); break;
    case 'AASB_138': result = validateAASB138(read.header, read.data, safeDate); break;
    case 'AASB_136': result = validateAASB136(read.header, read.data, safeDate); break;
    case 'AASB_124': result = validateAASB124(read.header, read.data, safeDate); break;
    case 'AASB_133': result = validateAASB133(read.header, read.data, safeDate); break;
    case 'AASB_8':   result = validateAASB8(read.header, read.data, safeDate); break;
    case 'AASB_9':   result = validateAASB9(read.header, read.data, safeDate); break;
    case 'AASB_112': result = validateAASB112(read.header, read.data, safeDate); break;
    case 'AASB_107': result = validateAASB107(read.header, read.data, safeDate); break;
    case 'AASB_121': result = validateAASB121(read.header, read.data, safeDate); break;
    case 'AASB_123': result = validateAASB123(read.header, read.data, safeDate); break;
    case 'AASB_1058': result = validateAASB1058(read.header, read.data, safeDate); break;
    default: return { ok: false, error: `Unhandled standard: ${input.standard}` };
  }
  return { ok: true, data: { sheet: read.sheet, ...result } };
}

// ---------- PHASE 2.1 — BATCH TOOLS ----------
// One LLM tool call → one Excel.run → many primitive operations. Replaces dozens
// of small tool calls per dashboard/report. The agent should reach for these by
// default; the singular variants stay for one-off cases.

function colLetter(idx: number): string {
  let c = idx;
  let letters = '';
  do {
    letters = String.fromCharCode(65 + (c % 26)) + letters;
    c = Math.floor(c / 26) - 1;
  } while (c >= 0);
  return letters;
}

interface SectionFormat {
  address: string;
  font?: { bold?: boolean; italic?: boolean; size?: number; color?: string; name?: string };
  fill?: { color?: string };
  alignment?: { horizontal?: string; vertical?: string; wrapText?: boolean };
  borders?: { edges: string[]; style?: string; color?: string; weight?: string };
  numberFormat?: string;
  merge?: boolean;
  rowHeight?: number;
}

async function applySectionFormat(input: { sheet: string; sections: SectionFormat[] }): Promise<ToolResult> {
  if (!input.sections?.length) return { ok: false, error: 'sections is empty.' };
  if (input.sections.length > 200) return { ok: false, error: `Too many sections: ${input.sections.length} (max 200).` };

  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    sheet.load('name');
    await ctx.sync();
    if (sheet.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };

    const ranges = input.sections.map(s => sheet.getRange(s.address));
    // Pre-load rowCount/columnCount only for sections that need 2D numberFormat
    for (let i = 0; i < input.sections.length; i++) {
      if (input.sections[i].numberFormat) ranges[i].load(['rowCount', 'columnCount']);
    }
    await ctx.sync();

    const edgeMap: Record<string, Excel.BorderIndex[]> = {
      top: ['EdgeTop' as Excel.BorderIndex],
      bottom: ['EdgeBottom' as Excel.BorderIndex],
      left: ['EdgeLeft' as Excel.BorderIndex],
      right: ['EdgeRight' as Excel.BorderIndex],
      all: ['EdgeTop','EdgeBottom','EdgeLeft','EdgeRight'] as Excel.BorderIndex[],
      inside: ['InsideHorizontal','InsideVertical'] as Excel.BorderIndex[],
    };

    for (let i = 0; i < input.sections.length; i++) {
      const s = input.sections[i];
      const range = ranges[i];

      if (s.font) {
        if (s.font.bold != null) range.format.font.bold = s.font.bold;
        if (s.font.italic != null) range.format.font.italic = s.font.italic;
        if (s.font.size != null) range.format.font.size = s.font.size;
        if (s.font.color) range.format.font.color = s.font.color;
        if (s.font.name) range.format.font.name = s.font.name;
      }
      if (s.fill?.color) range.format.fill.color = s.fill.color;
      if (s.alignment) {
        if (s.alignment.horizontal) range.format.horizontalAlignment = HALIGN_MAP[s.alignment.horizontal];
        if (s.alignment.vertical) range.format.verticalAlignment = VALIGN_MAP[s.alignment.vertical];
        if (s.alignment.wrapText != null) range.format.wrapText = s.alignment.wrapText;
      }
      if (s.numberFormat) {
        const fmt: string[][] = [];
        for (let r = 0; r < range.rowCount; r++) {
          fmt.push(new Array(range.columnCount).fill(s.numberFormat));
        }
        range.numberFormat = fmt;
      }
      if (s.borders?.edges?.length) {
        const style = BORDER_STYLE_MAP[s.borders.style ?? 'continuous'];
        const weight = BORDER_WEIGHT_MAP[s.borders.weight ?? 'thin'];
        const color = s.borders.color ?? '#000000';
        const indices = new Set<Excel.BorderIndex>();
        for (const edge of s.borders.edges) for (const idx of (edgeMap[edge] ?? [])) indices.add(idx);
        for (const idx of indices) {
          const b = range.format.borders.getItem(idx);
          b.style = style;
          b.weight = weight;
          b.color = color;
        }
      }
      if (s.merge) range.merge(true);
      if (s.rowHeight != null) range.format.rowHeight = s.rowHeight;
    }
    await ctx.sync();
    return { ok: true, data: { sheet: sheet.name, applied: input.sections.length } };
  });
}

async function applyConditionalFormatsBulk(input: { sheet: string; rules: any[] }): Promise<ToolResult> {
  if (!input.rules?.length) return { ok: false, error: 'rules is empty.' };
  if (input.rules.length > 100) return { ok: false, error: `Too many rules: ${input.rules.length} (max 100).` };

  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    sheet.load('name');
    await ctx.sync();
    if (sheet.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };

    const typeMap: Record<string, Excel.ConditionalFormatType> = {
      colorScale: 'ColorScale' as Excel.ConditionalFormatType,
      dataBar: 'DataBar' as Excel.ConditionalFormatType,
      iconSet: 'IconSet' as Excel.ConditionalFormatType,
      cellValue: 'CellValue' as Excel.ConditionalFormatType,
      topBottom: 'TopBottom' as Excel.ConditionalFormatType,
    };
    const opMap: Record<string, Excel.ConditionalCellValueOperator> = {
      greaterThan: 'GreaterThan' as Excel.ConditionalCellValueOperator,
      lessThan: 'LessThan' as Excel.ConditionalCellValueOperator,
      greaterThanOrEqual: 'GreaterThanOrEqual' as Excel.ConditionalCellValueOperator,
      lessThanOrEqual: 'LessThanOrEqual' as Excel.ConditionalCellValueOperator,
      equalTo: 'EqualTo' as Excel.ConditionalCellValueOperator,
      notEqualTo: 'NotEqualTo' as Excel.ConditionalCellValueOperator,
      between: 'Between' as Excel.ConditionalCellValueOperator,
      notBetween: 'NotBetween' as Excel.ConditionalCellValueOperator,
    };
    const iconStyleMap: Record<string, Excel.IconSet> = {
      ThreeArrows: 'ThreeArrows' as Excel.IconSet,
      ThreeTrafficLights1: 'ThreeTrafficLights1' as Excel.IconSet,
      ThreeSymbols2: 'ThreeSymbols2' as Excel.IconSet,
      ThreeFlags: 'ThreeFlags' as Excel.IconSet,
      FiveArrows: 'FiveArrows' as Excel.IconSet,
      FourArrows: 'FourArrows' as Excel.IconSet,
    };
    const tbMap: Record<string, Excel.ConditionalTopBottomCriterionType> = {
      topItems: 'TopItems' as Excel.ConditionalTopBottomCriterionType,
      bottomItems: 'BottomItems' as Excel.ConditionalTopBottomCriterionType,
      topPercent: 'TopPercent' as Excel.ConditionalTopBottomCriterionType,
      bottomPercent: 'BottomPercent' as Excel.ConditionalTopBottomCriterionType,
    };

    const failures: { index: number; error: string }[] = [];
    for (let i = 0; i < input.rules.length; i++) {
      const r = input.rules[i];
      try {
        const range = sheet.getRange(r.address);
        const cf = range.conditionalFormats.add(typeMap[r.type]);
        if (r.type === 'colorScale') {
          const minColor = r.colorScale?.minColor ?? '#F8696B';
          const midColor = r.colorScale?.midColor ?? '#FFEB84';
          const maxColor = r.colorScale?.maxColor ?? '#63BE7B';
          cf.colorScale!.criteria = {
            minimum: { type: 'LowestValue' as Excel.ConditionalFormatColorCriterionType, color: minColor },
            midpoint: { type: 'Percentile' as Excel.ConditionalFormatColorCriterionType, formula: '50', color: midColor },
            maximum: { type: 'HighestValue' as Excel.ConditionalFormatColorCriterionType, color: maxColor },
          };
        } else if (r.type === 'dataBar') {
          cf.dataBar!.positiveFormat.fillColor = r.dataBar?.color ?? '#5B9BD5';
        } else if (r.type === 'iconSet') {
          cf.iconSet!.style = iconStyleMap[r.iconSet?.style ?? 'ThreeArrows'];
          if (r.iconSet?.reverse) cf.iconSet!.reverseIconOrder = true;
        } else if (r.type === 'cellValue') {
          const v1 = String(r.cellValue.value1 ?? '0');
          const v2 = String(r.cellValue.value2 ?? '0');
          cf.cellValue!.rule = { operator: opMap[r.cellValue.operator], formula1: v1, formula2: v2 };
          if (r.cellValue.font?.color) cf.cellValue!.format.font.color = r.cellValue.font.color;
          if (r.cellValue.font?.bold != null) cf.cellValue!.format.font.bold = r.cellValue.font.bold;
          if (r.cellValue.fill?.color) cf.cellValue!.format.fill.color = r.cellValue.fill.color;
        } else if (r.type === 'topBottom') {
          cf.topBottom!.rule = { type: tbMap[r.topBottom.rule], rank: r.topBottom.rank };
          if (r.topBottom.font?.color) cf.topBottom!.format.font.color = r.topBottom.font.color;
          if (r.topBottom.font?.bold != null) cf.topBottom!.format.font.bold = r.topBottom.font.bold;
          if (r.topBottom.fill?.color) cf.topBottom!.format.fill.color = r.topBottom.fill.color;
        }
      } catch (e) {
        failures.push({ index: i, error: (e as Error).message });
      }
    }
    await ctx.sync();
    return {
      ok: true,
      data: {
        sheet: sheet.name,
        applied: input.rules.length - failures.length,
        failed: failures.length,
        failures: failures.length ? failures : undefined,
      },
    };
  });
}

interface ChartSpec {
  type: string;
  sourceRange: string;
  title?: string;
  anchorCell?: string;
  widthCells?: number;
  heightCells?: number;
  showLegend?: boolean;
}

async function addChartsBulk(input: { sheet: string; charts: ChartSpec[] }): Promise<ToolResult> {
  if (!input.charts?.length) return { ok: false, error: 'charts is empty.' };
  if (input.charts.length > 30) return { ok: false, error: `Too many charts: ${input.charts.length} (max 30).` };

  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    sheet.load('name');
    await ctx.sync();
    if (sheet.isNullObject) return { ok: false, error: `Sheet not found: ${input.sheet}` };

    const typeMap: Record<string, Excel.ChartType> = {
      column: 'ColumnClustered' as Excel.ChartType,
      columnStacked: 'ColumnStacked' as Excel.ChartType,
      bar: 'BarClustered' as Excel.ChartType,
      barStacked: 'BarStacked' as Excel.ChartType,
      line: 'Line' as Excel.ChartType,
      lineMarkers: 'LineMarkers' as Excel.ChartType,
      pie: 'Pie' as Excel.ChartType,
      doughnut: 'Doughnut' as Excel.ChartType,
      area: 'Area' as Excel.ChartType,
      areaStacked: 'AreaStacked' as Excel.ChartType,
      combo: 'ColumnClustered' as Excel.ChartType,
      waterfall: 'Waterfall' as Excel.ChartType,
      scatter: 'XYScatter' as Excel.ChartType,
    };

    // Phase 1: load anchor positions for charts that supply anchorCell
    const anchorRanges: (Excel.Range | null)[] = input.charts.map(c =>
      c.anchorCell ? sheet.getRange(c.anchorCell) : null
    );
    for (const r of anchorRanges) if (r) r.load(['top', 'left']);
    await ctx.sync();

    // Phase 2: create charts
    const created: { name: string; type: string }[] = [];
    const failures: { index: number; error: string }[] = [];
    const chartObjs: Excel.Chart[] = [];
    for (let i = 0; i < input.charts.length; i++) {
      const c = input.charts[i];
      try {
        const sourceAddr = c.sourceRange.includes('!')
          ? c.sourceRange
          : `${sheet.name}!${c.sourceRange}`;
        const sourceSheetName = sourceAddr.split('!')[0].replace(/^'|'$/g, '');
        const dataRange = ctx.workbook.worksheets.getItem(sourceSheetName).getRange(sourceAddr.split('!').pop()!);
        const chart = sheet.charts.add(typeMap[c.type] ?? typeMap.column, dataRange, 'Auto' as Excel.ChartSeriesBy);
        if (c.title) {
          chart.title.text = c.title;
          chart.title.visible = true;
        }
        if (c.showLegend != null) chart.legend.visible = c.showLegend;
        const anchor = anchorRanges[i];
        if (anchor) {
          chart.top = anchor.top;
          chart.left = anchor.left;
        }
        chart.width = (c.widthCells ?? 8) * 64;
        chart.height = (c.heightCells ?? 14) * 15;
        chart.load('name');
        chartObjs.push(chart);
      } catch (e) {
        failures.push({ index: i, error: (e as Error).message });
      }
    }
    await ctx.sync();
    for (let i = 0; i < chartObjs.length; i++) {
      created.push({ name: chartObjs[i].name, type: input.charts[i]?.type ?? 'unknown' });
    }
    return {
      ok: true,
      data: {
        sheet: sheet.name,
        added: created.length,
        failed: failures.length,
        charts: created,
        failures: failures.length ? failures : undefined,
      },
    };
  });
}

async function addFormulasBulkTemplate(input: {
  sheet: string;
  startCell: string;
  rows: number;
  cols?: number;
  template: string;
}): Promise<ToolResult> {
  const cols = input.cols ?? 1;
  const rows = input.rows;
  if (!Number.isFinite(rows) || rows < 1 || cols < 1) {
    return { ok: false, error: 'rows and cols must be positive integers.' };
  }
  if (rows * cols > 50_000) {
    return { ok: false, error: `Template block too large: ${rows * cols} cells (max 50,000).` };
  }
  if (!input.template || !input.template.startsWith('=')) {
    return { ok: false, error: 'template must start with "=".' };
  }
  const m = input.startCell.match(/^([A-Z]+)(\d+)$/i);
  if (!m) return { ok: false, error: 'startCell must be a single A1 cell, e.g. "B5".' };
  const baseRow = parseInt(m[2], 10);
  const baseColIdx = letterToCol(m[1]);

  const grid: string[][] = [];
  for (let i = 0; i < rows; i++) {
    const r = baseRow + i;
    const rowVals: string[] = [];
    for (let j = 0; j < cols; j++) {
      const c = colLetter(baseColIdx + j);
      const formula = input.template
        .replace(/\{row\}/g, String(r))
        .replace(/\{col\}/g, c)
        .replace(/\{i\}/g, String(i))
        .replace(/\{j\}/g, String(j));
      rowVals.push(formula);
    }
    grid.push(rowVals);
  }

  return Excel.run(async (ctx) => {
    let sheet = ctx.workbook.worksheets.getItemOrNullObject(input.sheet);
    await ctx.sync();
    if (sheet.isNullObject) {
      sheet = ctx.workbook.worksheets.add(input.sheet);
      await ctx.sync();
    }
    const start = sheet.getRange(input.startCell);
    const range = start.getResizedRange(rows - 1, cols - 1);
    range.formulas = grid;
    range.load('address');
    sheet.load('name');
    await ctx.sync();
    return {
      ok: true,
      data: {
        sheet: sheet.name,
        range: range.address.split('!').pop() ?? range.address,
        rows,
        cols,
        sample: grid[0]?.[0] ?? '',
      },
    };
  });
}

// ---------- ATOMIC PLAN EXECUTOR (Move 1 Week 3) ----------
//
// Runs an entire dashboard build inside one Excel.run. Replaces the
// 4-6 sequential tool calls (write_sheet -> apply_section_format ->
// add_charts_bulk -> freeze_panes -> set_column_widths) with a single
// declarative plan. Reduces wall time and eliminates per-call overhead.

import { executePlan, PlanOp } from '../services/planExecutor';
// Move 5 Step 8 — wizard commentary call needs to carry the same SSO
// bootstrap token as /api/chat and /api/documents now that the
// /api/workbook/commentary route is auth-gated.
import { getBootstrapToken } from '../services/sso';
// Phase C.3 — session log uses the friendly tool labels in the "Action"
// column so the log reads as human-readable history, not technical names.
import { toolLabel } from './labels';

async function buildScaffoldTool(input: Record<string, unknown>): Promise<ToolResult> {
  if (!activeWorkbookSessionId) {
    return { ok: false, error: 'No workbook session is active. Click "Ingest workbook" first.' };
  }
  if (!input?.archetype) {
    return { ok: false, error: 'archetype required (e.g. "pl_variance_monthly")' };
  }
  // 1. Fetch the deterministic plan from backend
  let plan: PlanOp[];
  let scaffoldNotes: string[] = [];
  let scaffoldWarnings: string[] = [];
  let commentarySlot: { sheet: string; startCell: string; rows: number; cols: number; title?: string } | undefined;
  try {
    const scaffoldToken = await getBootstrapToken();
    const resp = await fetch(`${QUERY_API_BASE_URL}/api/workbook/scaffold`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${scaffoldToken}`,
      },
      body: JSON.stringify({ sessionId: activeWorkbookSessionId, ...input }),
      credentials: 'include',  // Move 5 follow-up Step 14 — ALB stickiness cookie
    });
    if (!resp.ok) {
      let msg = `build_scaffold HTTP ${resp.status}`;
      try { const body = await resp.json(); if (body?.error) msg = body.error; } catch { /* keep status */ }
      checkSessionExpired(msg);
      return { ok: false, error: msg };
    }
    const data = await resp.json();
    plan = data.plan as PlanOp[];
    scaffoldNotes = data.notes ?? [];
    scaffoldWarnings = data.warnings ?? [];
    commentarySlot = data.commentarySlot;            // Move 5 Step 4
  } catch (e) {
    return { ok: false, error: `build_scaffold network error: ${(e as Error).message}` };
  }
  // 2. Execute the plan
  const exec = await executePlan(plan);
  if (!exec.ok) {
    return {
      ok: false,
      error: `Scaffold plan failed at op[${exec.error?.op_index}] (${exec.error?.op_type}): ${exec.error?.message}.`,
      data: {
        archetype: input.archetype,
        op_index: exec.error?.op_index,
        op_type: exec.error?.op_type,
        error_code: exec.error?.error_code,
        op_payload: exec.error?.op_payload,
        office_debug: exec.error?.office_debug,
        opsCompleted: exec.opsCompleted,
        opsTotal: exec.opsTotal,
      },
    };
  }
  return {
    ok: true,
    data: {
      archetype: input.archetype,
      opsCompleted: exec.opsCompleted,
      durationMs: exec.durationMs,
      notes: scaffoldNotes,
      warnings: scaffoldWarnings,
      validation: exec.validation,
      build_complete: exec.validation?.passed === true,
      commentarySlot,                                // Move 5 Step 4 — surface so wizard can patch it
      ...(exec.validation?.passed === false
        ? {
            agent_action_required:
              'validation.passed is FALSE. Surface the issues in validation.issues to the user — do not claim the dashboard is complete.',
          }
        : {}),
    },
  };
}

// ---------- Move 5 Step 4 — Commentary patch ----------
//
// Runs AFTER a wizard-driven build succeeds. Pulls 2-3 paragraphs of
// executive narrative from /api/workbook/commentary and writes them into
// the cell range the builder reserved (commentarySlot). Failure here
// never breaks the build — the dashboard is already on the page; we
// just leave the placeholder untouched and tell the user what happened.

interface CommentaryPatchResult {
  ok: boolean;
  paragraphs?: string[];
  modelId?: string;
  inputTokens?: number;
  outputTokens?: number;
  durationMs?: number;
  warnings?: string[];
  error?: string;
}

async function patchCommentary(
  archetype: string,
  theme: string | undefined,
  slot: { sheet: string; startCell: string; rows: number; cols: number; title?: string },
  userProfile?: { customInstructions?: string },
): Promise<CommentaryPatchResult> {
  if (!activeWorkbookSessionId) {
    return { ok: false, error: 'No active workbook session — cannot generate commentary.' };
  }
  // Move 5 Step 8 — Step 6 (B2) auth-gated /api/workbook/commentary, but
  // the original Step 4 fetch here was sending no Authorization header,
  // so the wizard with commentary ON would 401 in dev AND prod. Now mirrors
  // the chat / documents pattern: bootstrap token in a Bearer header.
  // If SSO is unavailable (e.g. user hasn't completed consent), surface a
  // clean "auth not ready" error rather than a stack trace.
  let token: string;
  try {
    token = await getBootstrapToken();
  } catch (e) {
    return { ok: false, error: `commentary auth error: ${(e as Error).message ?? 'no SSO token available'}` };
  }
  let result: CommentaryPatchResult;
  try {
    const resp = await fetch(`${QUERY_API_BASE_URL}/api/workbook/commentary`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        sessionId: activeWorkbookSessionId,
        archetype,
        theme,
        ...(userProfile ? { userProfile } : {}),
      }),
      credentials: 'include',  // Move 5 follow-up Step 14 — ALB stickiness cookie
    });
    if (!resp.ok) {
      let msg = `commentary HTTP ${resp.status}`;
      try { const b = await resp.json(); if (b?.error) msg = b.error; } catch { /* keep status */ }
      checkSessionExpired(msg);
      return { ok: false, error: msg };
    }
    const data = await resp.json();
    result = {
      ok: true,
      paragraphs: data.paragraphs ?? [],
      modelId: data.modelId,
      inputTokens: data.inputTokens,
      outputTokens: data.outputTokens,
      durationMs: data.durationMs,
      warnings: data.warnings ?? [],
    };
  } catch (e) {
    return { ok: false, error: `commentary network error: ${(e as Error).message}` };
  }

  if (!result.paragraphs || result.paragraphs.length === 0) {
    return { ...result, ok: false, error: 'Commentary endpoint returned no paragraphs.' };
  }

  // Write the paragraphs into the reserved range. The builder's
  // placeCommentary helper rendered a 4-row block; we collapse the
  // generated text into the first cell as a single wrapped paragraph
  // separated by line breaks. Excel's wrap-text on the merged cell
  // handles the layout. This avoids fighting the existing merge.
  const text = result.paragraphs.join('\n\n');
  const writePlan: PlanOp[] = [
    {
      op: 'write_values',
      sheet: slot.sheet,
      startCell: slot.startCell,
      values: [[text]],
    },
  ];
  const exec = await executePlan(writePlan);
  if (!exec.ok) {
    return { ...result, ok: false, error: `commentary write failed: ${exec.error?.message ?? 'unknown error'}` };
  }
  return result;
}

async function applyWorkbookPlanTool(rawInput: unknown): Promise<ToolResult> {
  // Move 4 Sprint 1 — agent-actionable validation feedback.
  //
  // Move 4 hotfix #7 — accept several common malformed shapes that the
  // agent has been emitting during refinement turns, and surface rich
  // diagnostic data when no shape can be salvaged. The agent could
  // previously only see the error TEXT ("plan must be an array...") and
  // had no way to see what shape it actually sent vs what was expected.
  let planArr: PlanOp[] | null = null;
  let unwrapNote: string | undefined;

  if (Array.isArray(rawInput)) {
    // Agent sent the bare array (forgot to wrap in { plan: ... })
    planArr = rawInput as PlanOp[];
    unwrapNote = 'auto-unwrapped: agent sent bare array; expected { plan: [...] }';
  } else if (rawInput && typeof rawInput === 'object') {
    const obj = rawInput as Record<string, unknown>;
    if (Array.isArray(obj.plan)) {
      planArr = obj.plan as PlanOp[];
    } else if (obj.plan && typeof obj.plan === 'object' && Array.isArray((obj.plan as Record<string, unknown>).plan)) {
      // Double-wrapped: { plan: { plan: [...] } }
      planArr = (obj.plan as Record<string, unknown>).plan as PlanOp[];
      unwrapNote = 'auto-unwrapped: nested { plan: { plan: [...] } }; flatten to one level';
    } else if (Array.isArray(obj.ops)) {
      planArr = obj.ops as PlanOp[];
      unwrapNote = 'auto-unwrapped: agent used "ops" key; correct key is "plan"';
    } else if (Array.isArray(obj.operations)) {
      planArr = obj.operations as PlanOp[];
      unwrapNote = 'auto-unwrapped: agent used "operations" key; correct key is "plan"';
    }
  }

  if (!planArr) {
    // Build rich diagnostic so the agent can see what it sent and fix it
    let preview: string;
    try {
      preview = JSON.stringify(rawInput).slice(0, 500);
    } catch {
      preview = String(rawInput).slice(0, 500);
    }
    const inputType = Array.isArray(rawInput) ? 'array' : typeof rawInput;
    const inputKeys = rawInput && typeof rawInput === 'object' && !Array.isArray(rawInput)
      ? Object.keys(rawInput as Record<string, unknown>)
      : [];
    return {
      ok: false,
      error: 'apply_workbook_plan: could not extract a plan array from input.',
      data: {
        received_input_type: inputType,
        received_input_keys: inputKeys,
        received_input_preview: preview,
        expected_shape: '{ "plan": [{"op": "create_sheet", "name": "Dashboard"}, {"op": "write_values", ...}, ...] }',
        rules: [
          'Top-level field name is "plan" (not "ops", "operations", or anything else).',
          'Value of "plan" is a FLAT array of op objects. Do not wrap the array in another object.',
          'Each array item is an object with an "op" field that names the op type.',
          'Op type must be one of: create_sheet, write_values, write_formulas_template, format_section, conditional_format, add_chart, add_sparkline, add_named_range, add_table, add_slicer, add_text_box, freeze_panes, set_column_widths, set_row_heights, data_validation_list, activate_sheet.',
        ],
        common_mistakes_observed: [
          'Wrapping inside another plan: { "plan": { "plan": [...] } } — flatten one level.',
          'Using "ops" or "operations" key: { "ops": [...] } — rename to "plan".',
          'Sending the bare array: [...] — wrap as { "plan": [...] }.',
          'Embedding ops as map values instead of array: { "plan": { "0": {...}, "1": {...} } } — must be a real array.',
        ],
      },
    };
  }
  if (planArr.length === 0) {
    return { ok: false, error: 'apply_workbook_plan: plan array is empty (no ops to execute).' };
  }
  // Move 5 follow-up Step 13 — bumped from 200 to 2000 (2026-05-10).
  // 200 was firing on legitimate forecast-model and 3-statement builds
  // (FY27 forecast = ~1200 ops: 300 rows × 4 ops/row). The agent
  // hallucinated this as 'serialisation issue' / 'plan being dropped'
  // instead of relaying the actual error, leading to multi-turn retry
  // loops the founder paid for. 2000 is a safety guardrail, not a
  // workflow target — the agent's natural 4096 output-token budget for
  // the tool_use input JSON prevents most runaway scenarios anyway.
  // Excel.run() handles thousands of ops per sync; 2000 × ~200 B JSON
  // = ~400 KB tool-use payload which is well under Bedrock's input cap.
  if (planArr.length > 2000) {
    return { ok: false, error: `apply_workbook_plan: plan has ${planArr.length} ops, max 2000. Trim or split.` };
  }
  if (unwrapNote) {
    console.warn(`[applyWorkbookPlanTool] ${unwrapNote}`);
  }
  const result = await executePlan(planArr);
  if (!result.ok) {
    // Surface the FULL structured error to the agent so it can fix the
    // specific op and re-emit. The op_payload is included so the agent
    // sees the exact shape it sent — easier to spot the problem than from
    // the message alone. office_debug carries Office.js-specific details
    // (code, errorLocation, fullStatement) when available — added in
    // hotfix #6 so the agent gets the actual API failure cause.
    return {
      ok: false,
      error: `apply_workbook_plan failed at op[${result.error?.op_index}] (${result.error?.op_type}): ${result.error?.message}`,
      data: {
        op_index: result.error?.op_index,
        op_type: result.error?.op_type,
        error_code: result.error?.error_code,
        op_payload: result.error?.op_payload,
        office_debug: result.error?.office_debug,
        opsCompleted: result.opsCompleted,
        opsTotal: result.opsTotal,
        durationMs: result.durationMs,
        recovery_hint:
          'Fix the specific op shown in op_payload and re-emit the corrected plan. Do NOT switch to per-op tools — they are not available in this mode.',
      },
    };
  }
  // Execution succeeded. Now report validation result. If validation failed,
  // ok stays true (the writes happened) but `validation.passed` is false and
  // the agent must address the issues before claiming completion.
  return {
    ok: true,
    data: {
      opsCompleted: result.opsCompleted,
      opsTotal: result.opsTotal,
      durationMs: result.durationMs,
      validation: result.validation,
      // Make the pass/fail decision dead-obvious in tool_result text.
      build_complete: result.validation?.passed === true,
      // Move 4 hotfix #7 — when we auto-unwrapped a malformed input, tell
      // the agent so it learns the correct shape for next time.
      ...(unwrapNote ? { input_shape_warning: unwrapNote, hint: 'For future calls, send { "plan": [...] } directly — no extra wrapping.' } : {}),
      ...(result.validation?.passed === false
        ? {
            agent_action_required:
              'validation.passed is FALSE. Address the issues in validation.issues before telling the user the build is complete. If you cannot fix them, surface them honestly to the user.',
          }
        : {}),
    },
  };
}

// ---------- INGESTED-WORKBOOK QUERY (Move 1 Week 2) ----------
//
// When the agent calls query_snapshot, the frontend acts as a thin proxy:
// it injects the sessionId from conversation state, posts to the backend's
// /api/workbook/query, and returns the result as a tool_result. Backend
// does the actual lookup against the in-memory snapshot in <10ms.
//
// The sessionId is set by App.tsx via the public setQuerySnapshotSession
// hook below — keeps office.ts free of React/state imports.

let activeWorkbookSessionId: string | null = null;
const QUERY_API_BASE_URL = (process.env.API_BASE_URL || 'http://localhost:3001');

export function setQuerySnapshotSession(sessionId: string | null): void {
  activeWorkbookSessionId = sessionId;
}

// ---------- Move 5 follow-up Step 12 — session expiration detection ----------
//
// The backend snapshot store has a 60-minute TTL. After expiry, any tool
// call against a stale sessionId gets HTTP 404 with error "Session not
// found or expired. Re-ingest the workbook." Today the agent surfaces
// that as text in the chat; the frontend keeps showing the green
// ✓ INGESTED panel from localStorage, leading to confusion about whether
// to re-ingest or not.
//
// This callback lets App.tsx auto-clear its ingest state + show a
// banner the moment any backend call reports the snapshot is gone.
// One callback shared across every backend-hitting tool dispatcher.

let sessionExpiredCallback: (() => void) | null = null;

export function setSessionExpiredCallback(cb: (() => void) | null): void {
  sessionExpiredCallback = cb;
}

const SESSION_EXPIRED_RE = /session not found or expired/i;

function checkSessionExpired(errorMsg: string | null | undefined): void {
  if (typeof errorMsg === 'string' && SESSION_EXPIRED_RE.test(errorMsg)) {
    sessionExpiredCallback?.();
    // Also clear the local active sessionId so subsequent tool calls
    // surface a clean "no workbook session" error instead of repeatedly
    // hitting the same expired sessionId.
    activeWorkbookSessionId = null;
  }
}

// ---------- Move 5 Step 1 — Dashboard Wizard public entry point ----------
//
// Bypasses the agent loop. The questionnaire collects answers, maps them
// deterministically to a scaffold request, and we run the same backend
// builder + plan executor that the agent uses — just without spending
// tokens on archetype guessing. Returns a typed shape for App.tsx.

import type { ValidationReport } from '../services/planExecutor';

export interface WizardBuildResult {
  ok: boolean;
  archetype: string;
  theme?: string;
  opsCompleted?: number;
  durationMs?: number;
  /** Move 5 Step 1 — surfaces the same ValidationReport produced by planExecutor.runValidationPass. */
  validation?: ValidationReport;
  build_complete?: boolean;
  warnings?: string[];
  notes?: string[];
  error?: string;
  errorDetails?: {
    op_index?: number;
    op_type?: string;
    error_code?: string;
  };
  /** Move 5 Step 4 — outcome of the optional LLM commentary patch. */
  commentary?: {
    ok: boolean;
    paragraphsWritten?: number;
    modelId?: string;
    durationMs?: number;
    warnings?: string[];
    error?: string;
  };
}

/**
 * Phase B.4 — /commentary slash skill.
 *
 * Public wrapper over the /api/workbook/commentary endpoint. Returns the
 * generated paragraphs without writing them to a sheet (the slash skill
 * surfaces them as a chat bubble, the wizard's slot-patching path handles
 * the in-sheet rendering separately).
 */
export interface StandaloneCommentaryResult {
  ok: boolean;
  paragraphs?: string[];
  modelId?: string;
  durationMs?: number;
  warnings?: string[];
  error?: string;
}
export async function runCommentary(opts: {
  archetype?: string;
  theme?: string;
  /** Phase C.2 — forwarded so commentary respects the same custom instructions as chat. */
  userProfile?: { customInstructions?: string };
} = {}): Promise<StandaloneCommentaryResult> {
  if (!activeWorkbookSessionId) {
    return { ok: false, error: 'No active workbook session — click "⬆ Ingest workbook" first.' };
  }
  let token: string;
  try {
    token = await getBootstrapToken();
  } catch (e) {
    return { ok: false, error: `commentary auth error: ${(e as Error).message ?? 'no SSO token available'}` };
  }
  try {
    const resp = await fetch(`${QUERY_API_BASE_URL}/api/workbook/commentary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        sessionId: activeWorkbookSessionId,
        archetype: opts.archetype ?? 'board_pl_monthly',
        theme: opts.theme,
        ...(opts.userProfile ? { userProfile: opts.userProfile } : {}),
      }),
      credentials: 'include',
    });
    if (!resp.ok) {
      let msg = `commentary HTTP ${resp.status}`;
      try { const b = await resp.json(); if (b?.error) msg = b.error; } catch { /* keep status */ }
      checkSessionExpired(msg);
      return { ok: false, error: msg };
    }
    const data = await resp.json();
    return {
      ok: true,
      paragraphs: data.paragraphs ?? [],
      modelId: data.modelId,
      durationMs: data.durationMs,
      warnings: data.warnings ?? [],
    };
  } catch (e) {
    return { ok: false, error: `commentary network error: ${(e as Error).message}` };
  }
}

export async function buildDashboardFromWizard(
  request: {
    archetype: string;
    theme?: string;
    kpiCount?: number;
    topN?: number;
    includeCommentary?: boolean;
    includeAuditPanel?: boolean;
    /** Phase C.2 — forwarded to patchCommentary so wizard commentary honours custom instructions. */
    userProfile?: { customInstructions?: string };
  },
): Promise<WizardBuildResult> {
  if (!activeWorkbookSessionId) {
    return {
      ok: false,
      archetype: request.archetype,
      error: 'No workbook session is active. Click "⬆ Ingest workbook" first.',
    };
  }
  // Reuse the same internal function the agent uses — single code path
  // for backend dispatch + plan execution + post-render validation.
  const result = await buildScaffoldTool(request as Record<string, unknown>);
  if (!result.ok) {
    const data = result.data as { op_index?: number; op_type?: string; error_code?: string } | undefined;
    return {
      ok: false,
      archetype: request.archetype,
      error: result.error ?? 'Build failed',
      errorDetails: data ? { op_index: data.op_index, op_type: data.op_type, error_code: data.error_code } : undefined,
    };
  }
  const data = result.data as {
    archetype: string;
    opsCompleted: number;
    durationMs: number;
    notes?: string[];
    warnings?: string[];
    validation?: WizardBuildResult['validation'];
    build_complete?: boolean;
    commentarySlot?: { sheet: string; startCell: string; rows: number; cols: number; title?: string };
  };

  // Move 5 Step 4 — optional commentary patch. Only fires when the wizard
  // asked for it AND the builder reserved a slot. Failures here are
  // surfaced but never fail the overall build — the dashboard is already
  // on the page and the placeholder block stays in place if the LLM call
  // doesn't land.
  let commentary: WizardBuildResult['commentary'];
  if (request.includeCommentary && data.commentarySlot) {
    const cp = await patchCommentary(data.archetype, request.theme, data.commentarySlot, request.userProfile);
    commentary = {
      ok: cp.ok,
      paragraphsWritten: cp.paragraphs?.length,
      modelId: cp.modelId,
      durationMs: cp.durationMs,
      warnings: cp.warnings,
      error: cp.error,
    };
  } else if (request.includeCommentary && !data.commentarySlot) {
    commentary = {
      ok: false,
      error: `Archetype "${data.archetype}" does not reserve a commentary slot yet. The placeholder block stays in place — edit it manually or pick a different archetype.`,
    };
  }

  return {
    ok: true,
    archetype: data.archetype,
    theme: request.theme,
    opsCompleted: data.opsCompleted,
    durationMs: data.durationMs,
    validation: data.validation,
    build_complete: data.build_complete,
    warnings: data.warnings,
    notes: data.notes,
    commentary,
  };
}

async function getDomainReferenceProxy(input: Record<string, unknown>): Promise<ToolResult> {
  if (!input?.category) {
    return { ok: false, error: 'category required (regulatory | kpi_menu | list)' };
  }
  try {
    const domainToken = await getBootstrapToken();
    const resp = await fetch(`${QUERY_API_BASE_URL}/api/workbook/domain`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${domainToken}`,
      },
      body: JSON.stringify(input),
      credentials: 'include',  // Move 5 follow-up Step 14 — ALB stickiness cookie
    });
    if (!resp.ok) {
      let msg = `get_domain_reference HTTP ${resp.status}`;
      try { const body = await resp.json(); if (body?.error) msg = body.error; } catch { /* keep status */ }
      return { ok: false, error: msg };
    }
    const data = await resp.json();
    return { ok: true, data };
  } catch (e) {
    return { ok: false, error: `get_domain_reference network error: ${(e as Error).message}` };
  }
}

async function querySnapshotProxy(input: Record<string, unknown>): Promise<ToolResult> {
  if (!activeWorkbookSessionId) {
    return {
      ok: false,
      error: 'No workbook session is active. Click "Ingest workbook" to upload a snapshot, or fall back to snapshot_sheet for the legacy flow.',
    };
  }
  try {
    const queryToken = await getBootstrapToken();
    const resp = await fetch(`${QUERY_API_BASE_URL}/api/workbook/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${queryToken}`,
      },
      body: JSON.stringify({ sessionId: activeWorkbookSessionId, ...input }),
      credentials: 'include',  // Move 5 follow-up Step 14 — ALB stickiness cookie
    });
    if (!resp.ok) {
      let msg = `query_snapshot HTTP ${resp.status}`;
      try { const body = await resp.json(); if (body?.error) msg = body.error; } catch { /* keep status */ }
      checkSessionExpired(msg);
      return { ok: false, error: msg };
    }
    const data = await resp.json();
    return { ok: true, data };
  } catch (e) {
    return { ok: false, error: `query_snapshot network error: ${(e as Error).message}` };
  }
}

// ============= ASK_USER (Phase A.3 — clickable clarifying questions) =============
//
// When the agent emits ask_user({question, options[]}), the tool BLOCKS the
// agent loop until the user clicks an option in the UI. The dispatched
// handler returns a Promise that the UI later resolves via resolveAskUser().
// The pending question is stored keyed by tool_use_id so the chat bubble
// can render the right buttons in the right place when the agent makes
// multiple ask_user calls in a turn (rare but possible).
//
// Lifecycle: on "new chat" / "switch conversation" / "cancel chat", the
// UI must call clearPendingAskUser() to reject any orphaned promise.

interface PendingAskUser {
  toolUseId: string;
  question: string;
  options: string[];
  /**
   * Pre-built snapshot returned by getPendingAskUser. Stored as a stable
   * reference so useSyncExternalStore sees the same object on every render
   * for the same underlying state. Without this, React 18 treats every
   * getSnapshot call as a state change and falls into a #185 loop.
   */
  snapshot: { question: string; options: string[] };
  resolve: (chosen: string) => void;
  reject: (reason: string) => void;
}

const pendingAskUserById = new Map<string, PendingAskUser>();
const pendingAskUserSubscribers = new Set<() => void>();

function notifyPendingSubscribers(): void {
  for (const cb of pendingAskUserSubscribers) {
    try { cb(); } catch { /* subscriber errors must not break others */ }
  }
}

/**
 * Returns the pending ask_user question keyed by tool_use_id, or null.
 *
 * IMPORTANT: this must return the SAME object reference across calls for
 * the same underlying state. useSyncExternalStore re-renders forever if
 * the snapshot reference changes without an actual state change.
 */
export function getPendingAskUser(toolUseId: string): { question: string; options: string[] } | null {
  const p = pendingAskUserById.get(toolUseId);
  return p ? p.snapshot : null;
}

/** UI calls this when the user clicks an option. */
export function resolveAskUser(toolUseId: string, chosen: string): boolean {
  const p = pendingAskUserById.get(toolUseId);
  if (!p) return false;
  if (!p.options.includes(chosen)) {
    // Defensive: only accept options the agent provided. Prevents the UI
    // from injecting arbitrary text into the agent's context.
    return false;
  }
  pendingAskUserById.delete(p.toolUseId);
  p.resolve(chosen);
  notifyPendingSubscribers();
  return true;
}

/** Subscribe to changes in the pending-question set. Returns an unsubscribe fn. */
export function subscribePendingAskUser(cb: () => void): () => void {
  pendingAskUserSubscribers.add(cb);
  return () => { pendingAskUserSubscribers.delete(cb); };
}

/** Reject every pending question. Called on "new chat" / cancel / conversation switch. */
export function clearPendingAskUser(reason = 'Question cancelled.'): void {
  for (const p of pendingAskUserById.values()) {
    try { p.reject(reason); } catch { /* swallow — caller already errored */ }
  }
  pendingAskUserById.clear();
  notifyPendingSubscribers();
}

async function askUser(input: { question?: unknown; options?: unknown }, toolUseId: string): Promise<ToolResult> {
  const question = typeof input?.question === 'string' ? input.question.trim() : '';
  if (!question) {
    return { ok: false, error: 'question is required (the clarification you want from the user).' };
  }
  if (question.length > 280) {
    return { ok: false, error: `question is too long: ${question.length} chars (max 280). Keep it to one short paragraph.` };
  }

  const rawOptions = Array.isArray(input?.options) ? input.options : [];
  const seen = new Set<string>();
  const options: string[] = [];
  for (const o of rawOptions) {
    if (typeof o !== 'string') continue;
    const trimmed = o.trim();
    if (!trimmed) continue;
    if (trimmed.length > 100) {
      return { ok: false, error: `option "${trimmed.slice(0, 40)}…" is ${trimmed.length} chars (max 100 per option).` };
    }
    if (seen.has(trimmed)) continue;
    seen.add(trimmed);
    options.push(trimmed);
  }
  if (options.length < 2) {
    return { ok: false, error: `options must contain at least 2 distinct non-empty strings (got ${options.length}). For open-ended questions ask in text instead.` };
  }
  if (options.length > 6) {
    return { ok: false, error: `options has ${options.length} entries (max 6). Reduce to the most important choices.` };
  }
  if (!toolUseId) {
    // Defensive — chat.ts always passes a toolUseId. Without one we can't
    // key the pending entry for the UI to look up.
    return { ok: false, error: 'ask_user requires a tool_use_id (internal wiring bug; please retry).' };
  }

  return new Promise<ToolResult>((resolve) => {
    const entry: PendingAskUser = {
      toolUseId,
      question,
      options,
      // Build the snapshot ONCE so getPendingAskUser returns a stable
      // reference across re-renders (Phase A.3 fix for React #185).
      snapshot: { question, options },
      resolve: (chosen: string) => resolve({ ok: true, data: { selected: chosen, question } }),
      reject: (reason: string) => resolve({ ok: false, error: reason }),
    };
    pendingAskUserById.set(toolUseId, entry);
    notifyPendingSubscribers();
  });
}

// ============= EXECUTE_OFFICE_JS (Phase A.1 escape hatch) =============
//
// Arbitrary Office.js code run inside Excel.run. The fallback path when
// neither build_scaffold's archetypes nor apply_workbook_plan's JSON op
// vocab can express what the user needs (row-offset logic, multi-pass
// read-then-write, conditional row counts derived from sync results).
//
// Threat model is the AGENT, not a human attacker. The regex tripwires
// below are NOT a security boundary — `new AsyncFunction(code)` runs in
// global scope and any of the blocked identifiers can be obtained via
// reflection (`globalThis['fe'+'tch']`, `[].constructor.constructor`, etc.).
// What the tripwires DO catch: an agent that drifts into reading via
// fetch, persisting to localStorage, or escaping to window. — i.e.
// confusion, not malice. The hard backstops are the size caps and the
// 30s timeout, which constrain blast radius regardless of intent.

const EXEC_MAX_CODE_CHARS   = 8_000;
const EXEC_TIMEOUT_MS       = 30_000;
const EXEC_MAX_RESULT_CHARS = 50_000;

const EXEC_FORBIDDEN: Array<{ pattern: RegExp; identifier: string }> = [
  { pattern: /\bfetch\s*\(/,            identifier: 'fetch' },
  { pattern: /\bXMLHttpRequest\b/,      identifier: 'XMLHttpRequest' },
  { pattern: /\bWebSocket\b/,           identifier: 'WebSocket' },
  { pattern: /\bEventSource\b/,         identifier: 'EventSource' },
  { pattern: /\bwindow\s*[.[]/,         identifier: 'window' },
  { pattern: /\bdocument\s*[.[]/,       identifier: 'document' },
  { pattern: /\blocalStorage\b/,        identifier: 'localStorage' },
  { pattern: /\bsessionStorage\b/,      identifier: 'sessionStorage' },
  { pattern: /\bindexedDB\b/,           identifier: 'indexedDB' },
  { pattern: /\blocation\s*[.[]/,       identifier: 'location' },
  { pattern: /\bnavigator\s*[.[]/,      identifier: 'navigator' },
  { pattern: /\bparent\s*[.[]/,         identifier: 'parent' },
  { pattern: /\btop\s*[.[]/,            identifier: 'top' },
  { pattern: /\bglobalThis\b/,          identifier: 'globalThis' },
  { pattern: /\bself\s*[.[]/,           identifier: 'self' },
  { pattern: /\beval\s*\(/,             identifier: 'eval()' },
  { pattern: /\bnew\s+Function\s*\(/,   identifier: 'new Function()' },
  { pattern: /\bimport\s*\(/,           identifier: 'import()' },
  { pattern: /\brequire\s*\(/,          identifier: 'require()' },
  { pattern: /\bprocess\s*[.[]/,        identifier: 'process' },
];

// Office.js proxy objects (Range, Worksheet, Chart, etc.) all carry a
// `context` ref to a RequestContext that has a `workbook` property. We
// use that as the heuristic; cycles in the proxy graph would otherwise
// cause JSON.stringify to throw. Substitute a readable placeholder so the
// agent gets a meaningful "you returned an unloaded Office object" signal
// rather than a stack trace.
function isOfficeProxy(v: unknown): string | null {
  if (!v || typeof v !== 'object') return null;
  const ctx = (v as { context?: unknown }).context;
  if (ctx && typeof ctx === 'object' && 'workbook' in (ctx as object)) {
    const name = (v as { constructor?: { name?: string } }).constructor?.name ?? 'OfficeObject';
    return name;
  }
  return null;
}

function safeSerialise(value: unknown): { json: string; truncated: boolean } {
  const seen = new WeakSet<object>();
  let json: string;
  try {
    json = JSON.stringify(value ?? null, (_key, v) => {
      const officeType = isOfficeProxy(v);
      if (officeType) return `[Office object: ${officeType} — load properties and return the loaded values, not the proxy]`;
      if (v && typeof v === 'object') {
        if (seen.has(v as object)) return '[circular]';
        seen.add(v as object);
      }
      if (typeof v === 'function') return `[function ${(v as { name?: string }).name || 'anonymous'}]`;
      if (typeof v === 'bigint') return v.toString();
      if (typeof v === 'undefined') return null;
      return v;
    }) ?? 'null';
  } catch (e) {
    // Final fallback if the replacer didn't catch a corner case.
    json = JSON.stringify({ note: 'value was not JSON-serialisable', error: (e as Error).message });
  }
  if (json.length > EXEC_MAX_RESULT_CHARS) {
    return {
      json: json.slice(0, EXEC_MAX_RESULT_CHARS) + `…[truncated ${json.length - EXEC_MAX_RESULT_CHARS} chars]`,
      truncated: true,
    };
  }
  return { json, truncated: false };
}

// AsyncFunction is the constructor for `async function` — equivalent to
// `new Function` but the body can use `await`. Not exposed by name in TS
// lib.d.ts, so we grab it off the prototype of an async function literal.
const AsyncFunctionCtor = Object.getPrototypeOf(async function noop(){}).constructor as new (
  ...args: string[]
) => (...args: unknown[]) => Promise<unknown>;

async function executeOfficeJs(input: { code?: string; description?: string }): Promise<ToolResult> {
  const code = typeof input?.code === 'string' ? input.code : '';
  const description = typeof input?.description === 'string' ? input.description.trim() : '';

  if (!description) {
    return { ok: false, error: 'description is required (a one-line summary of what the snippet does, e.g. "Build FY27 forecast on Forecast sheet").' };
  }
  if (!code.trim()) {
    return { ok: false, error: 'code is required (a non-empty JS snippet to run inside Excel.run).' };
  }
  if (code.length > EXEC_MAX_CODE_CHARS) {
    return { ok: false, error: `code too long: ${code.length} chars (max ${EXEC_MAX_CODE_CHARS}). Split into multiple execute_office_js calls or use apply_workbook_plan.` };
  }
  for (const { pattern, identifier } of EXEC_FORBIDDEN) {
    if (pattern.test(code)) {
      return { ok: false, error: `Forbidden identifier in code: '${identifier}'. execute_office_js exposes only the Excel.run \`context\` and the \`Excel\` namespace. If you were trying to read data, use query_snapshot; if you were trying to persist state, use the workbook itself.` };
    }
  }

  // Construct the async function once, outside Excel.run, so syntax errors
  // surface clearly before any workbook state changes.
  let fn: (context: Excel.RequestContext, ExcelArg: typeof Excel) => Promise<unknown>;
  try {
    fn = new AsyncFunctionCtor('context', 'Excel', code) as typeof fn;
  } catch (e) {
    return { ok: false, error: `code did not parse: ${(e as Error).message}` };
  }

  const started = Date.now();
  let userResult: unknown;
  try {
    userResult = await Excel.run(async (context) => {
      return await Promise.race<unknown>([
        fn(context, Excel),
        new Promise<never>((_, rej) =>
          setTimeout(() => rej(new Error(`execute_office_js exceeded ${EXEC_TIMEOUT_MS}ms time limit. Trim the snippet or split into multiple calls.`)), EXEC_TIMEOUT_MS)
        ),
      ]);
    });
  } catch (e) {
    return { ok: false, error: `execute_office_js threw: ${(e as Error).message}` };
  }
  const durationMs = Date.now() - started;

  const { json, truncated } = safeSerialise(userResult);
  let parsedResult: unknown;
  try { parsedResult = JSON.parse(json); } catch { parsedResult = json; }

  return {
    ok: true,
    data: {
      description,
      durationMs,
      codeLines: code.split('\n').length,
      truncated,
      result: parsedResult,
    },
  };
}

// ============= SESSION LOG (Phase C.3) =============
//
// When the user toggles "Auto session log" in Settings, every successful
// write-class tool appends a row to a `Practice IQ Log` sheet inside the
// workbook. Read tools are skipped (too noisy). Failures are logged with
// the error string so the user can audit what went wrong even after the
// chat scrolls past it.
//
// The log write itself uses Excel.run directly — it does NOT recurse
// through runTool, so there's no infinite-loop risk. We still guard with
// `logBusy` so two near-simultaneous tool completions don't race on the
// same getUsedRange() call.

let sessionLogEnabled = false;
let logBusy = false;
const LOG_SHEET_NAME = 'Practice IQ Log';

const LOG_WRITE_TOOLS = new Set<string>([
  'build_scaffold',
  'apply_workbook_plan',
  'execute_office_js',
  'write_from_snapshot',
  'create_sheet',
  'write_sheet',
  'add_formula',
  'add_formulas',
  'add_formulas_bulk_template',
  'export_to_pdf',
  'generate_word_memo',
  'generate_powerpoint_deck',
  'generate_disclosure_checklist',
]);

export function setSessionLogEnabled(enabled: boolean): void {
  sessionLogEnabled = enabled;
}

function describeToolResult(name: string, data: unknown, ok: boolean, errorMsg?: string): string {
  if (!ok) return errorMsg ? errorMsg.slice(0, 280) : 'failed';
  if (!data || typeof data !== 'object') return '';
  const d = data as Record<string, unknown>;
  // execute_office_js: rich description from the agent
  if (typeof d.description === 'string' && d.description.trim()) return d.description.trim();
  // apply_workbook_plan / build_scaffold: op counts + archetype
  if (typeof d.archetype === 'string' && typeof d.opsCompleted === 'number') {
    return `archetype=${d.archetype}, ${d.opsCompleted} op${d.opsCompleted === 1 ? '' : 's'}`;
  }
  if (typeof d.opsCompleted === 'number' && typeof d.opsTotal === 'number') {
    return `${d.opsCompleted}/${d.opsTotal} ops completed`;
  }
  if (typeof d.sheet === 'string') return `sheet=${d.sheet}`;
  if (typeof d.name === 'string') return `name=${d.name}`;
  return name;
}

async function appendSessionLogRow(name: string, friendly: string, summary: string, ok: boolean): Promise<void> {
  if (logBusy) {
    // Drop this entry rather than queue — losing a row is better than blocking
    // the next user action while we wait for the prior log write.
    return;
  }
  logBusy = true;
  try {
    await Excel.run(async (ctx) => {
      const wb = ctx.workbook;
      let logSheet = wb.worksheets.getItemOrNullObject(LOG_SHEET_NAME);
      logSheet.load(['name']);
      await ctx.sync();
      const created = logSheet.isNullObject;
      if (created) {
        logSheet = wb.worksheets.add(LOG_SHEET_NAME);
        const header = logSheet.getRange('A1:E1');
        header.values = [['Timestamp', 'Action', 'Tool', 'Summary', 'Result']];
        header.format.font.bold = true;
        header.format.fill.color = '#1F3864';
        header.format.font.color = '#FFFFFF';
        header.format.horizontalAlignment = 'Left';
        // Column widths
        logSheet.getRange('A:A').format.columnWidth = 140;
        logSheet.getRange('B:B').format.columnWidth = 180;
        logSheet.getRange('C:C').format.columnWidth = 200;
        logSheet.getRange('D:D').format.columnWidth = 420;
        logSheet.getRange('E:E').format.columnWidth = 60;
        // Freeze header
        logSheet.freezePanes.freezeRows(1);
      }
      const used = logSheet.getUsedRangeOrNullObject(true);
      used.load(['rowCount']);
      await ctx.sync();
      const nextRow = (used.isNullObject || !used.rowCount) ? 2 : used.rowCount + 1;
      const target = logSheet.getRange(`A${nextRow}:E${nextRow}`);
      const tsLocal = new Date().toLocaleString('en-AU', { hour12: false });
      target.values = [[
        tsLocal,
        friendly,
        name,
        summary.length > 400 ? summary.slice(0, 400) + '…' : summary,
        ok ? 'OK' : 'ERROR',
      ]];
      // Subtle severity tint on the result cell
      const resultCell = logSheet.getRange(`E${nextRow}`);
      resultCell.format.font.bold = true;
      resultCell.format.font.color = ok ? '#00734D' : '#C00000';
      await ctx.sync();
    });
  } catch (e) {
    // Logging must never break the user's actual work — swallow any error.
    console.warn('[session-log] append failed:', (e as Error).message);
  } finally {
    logBusy = false;
  }
}

// ---------- DISPATCHER ----------

const SHEET_GUARDED_READ_TOOLS = new Set([
  'read_range',
  'read_used_range',
  'read_formulas',
  'read_cell_types',
]);

export async function runTool(name: string, input: unknown, opts?: { toolUseId?: string }): Promise<{ content: string; isError: boolean }> {
  try {
    // Tier 2 fix #5 — block raw reads on already-snapshotted sheets. Forces
    // the agent onto compute_on_snapshot/describe_snapshot and prevents
    // 30K-token cell grids from being re-flowed into context.
    if (SHEET_GUARDED_READ_TOOLS.has(name)) {
      const target = await resolveTargetSheetName(input as { sheet?: string } | undefined);
      if (snapshottedSheets.has(normalizeSheetName(target))) {
        return {
          content: JSON.stringify({
            error: `Sheet "${target}" is already snapshotted in this conversation. Re-reading is forbidden — use compute_on_snapshot or describe_snapshot instead. If you need a value, call compute_on_snapshot with the snapshotId.`,
          }),
          isError: true,
        };
      }
    }

    let result: ToolResult;
    switch (name) {
      case 'get_workbook_info': result = await getWorkbookInfo(); break;
      case 'list_sheets':       result = await listSheets(); break;
      case 'read_selection':    result = await readSelection(); break;
      case 'read_range':        result = await readRange(input as any); break;
      case 'read_used_range':   result = await readUsedRange(input as any); break;
      // Phase 1 — snapshot-and-reference (Claude-for-Excel pattern)
      case 'snapshot_sheet':       result = await snapshotSheetTool(input as any); break;
      case 'scan_for_dashboard':   result = await scanForDashboardTool(input as any); break;
      case 'query_snapshot':       result = await querySnapshotProxy(input as Record<string, unknown>); break;
      case 'get_domain_reference': result = await getDomainReferenceProxy(input as Record<string, unknown>); break;
      case 'build_scaffold':       result = await buildScaffoldTool(input as Record<string, unknown>); break;
      case 'apply_workbook_plan':  result = await applyWorkbookPlanTool(input as any); break;
      // Phase A.1 — escape hatch for novel builds the JSON op vocab can't express.
      case 'execute_office_js':    result = await executeOfficeJs(input as any); break;
      // Phase A.3 — clickable clarifying questions. Blocks until the UI resolves it.
      case 'ask_user':             result = await askUser(input as any, opts?.toolUseId ?? ''); break;
      case 'describe_snapshot':    result = await describeSnapshotTool(input as any); break;
      case 'compute_on_snapshot':  result = await computeOnSnapshotTool(input as any); break;
      case 'write_from_snapshot':  result = await writeFromSnapshotTool(input as any); break;
      case 'create_sheet':      result = await createSheet(input as any); break;
      case 'write_sheet':       result = await writeSheet(input as any); break;
      case 'add_formula':       result = await addFormula(input as any); break;
      case 'add_formulas':      result = await addFormulas(input as any); break;
      case 'format_range':        result = await formatRange(input as any); break;
      case 'freeze_panes':        result = await freezePanes(input as any); break;
      case 'set_column_widths':   result = await setColumnWidths(input as any); break;
      // Phase 2.1 — batch tools
      case 'apply_section_format':            result = await applySectionFormat(input as any); break;
      case 'apply_conditional_formats_bulk':  result = await applyConditionalFormatsBulk(input as any); break;
      case 'add_charts_bulk':                 result = await addChartsBulk(input as any); break;
      case 'add_formulas_bulk_template':      result = await addFormulasBulkTemplate(input as any); break;
      case 'list_standards':      result = await listStandardsTool(); break;
      case 'detect_standard':     result = await detectStandardTool(input as any); break;
      case 'validate_standard':   result = await validateStandardTool(input as any); break;
      // Round 1 — deep read
      case 'read_formulas':            result = await readFormulas(input as any); break;
      case 'read_cell_types':          result = await readCellTypes(input as any); break;
      case 'read_workbook_structure':  result = await readWorkbookStructure(); break;
      case 'list_named_ranges':        result = await listNamedRanges(); break;
      case 'define_named_range':       result = await defineNamedRange(input as any); break;
      case 'list_tables':              result = await listTablesTool(); break;
      case 'create_table':             result = await createTableTool(input as any); break;
      case 'read_table':               result = await readTableTool(input as any); break;
      // Round 1 — structural
      case 'insert_rows':              result = await insertRows(input as any); break;
      case 'insert_columns':           result = await insertColumns(input as any); break;
      case 'delete_rows':              result = await deleteRows(input as any); break;
      case 'delete_columns':           result = await deleteColumns(input as any); break;
      case 'clear_range':              result = await clearRange(input as any); break;
      case 'copy_range':               result = await copyRange(input as any); break;
      case 'find_replace':             result = await findReplace(input as any); break;
      // Round 1 — advanced formatting
      case 'conditional_format':       result = await conditionalFormat(input as any); break;
      case 'add_chart':                result = await addChart(input as any); break;
      case 'add_data_validation':      result = await addDataValidation(input as any); break;
      // Round 1 — workbook / sheet management
      case 'set_calculation_mode':     result = await setCalculationMode(input as any); break;
      case 'set_sheet_visibility':     result = await setSheetVisibility(input as any); break;
      case 'set_sheet_tab_color':      result = await setSheetTabColor(input as any); break;
      case 'protect_sheet':            result = await protectSheet(input as any); break;
      case 'unprotect_sheet':          result = await unprotectSheet(input as any); break;
      case 'mark_input_cells':         result = await markInputCells(input as any); break;
      case 'hide_rows_columns':        result = await hideRowsColumns(input as any); break;
      case 'add_comment':              result = await addCommentTool(input as any); break;
      case 'sort_range':                result = await sortRange(input as any); break;
      case 'remove_duplicates':        result = await removeDuplicates(input as any); break;
      // Round 5 — document generation
      case 'export_to_pdf':             result = await exportToPdf(input as any); break;
      case 'generate_word_memo':        result = await generateWordMemo(input as any); break;
      case 'generate_powerpoint_deck':  result = await generatePowerPointDeck(input as any); break;
      case 'generate_disclosure_checklist': result = await disclosureChecklistTool(input as any); break;
      // Financial math
      case 'calculate_landed_cost':     result = await calculateLandedCostTool(input as any); break;
      case 'apply_cpi_escalation':      result = await applyCpiEscalationTool(input as any); break;
      case 'nominal_to_real':           result = await nominalToRealTool(input as any); break;
      case 'bond_value':                result = await bondValueTool(input as any); break;
      case 'yield_to_maturity':         result = await yieldToMaturityTool(input as any); break;
      case 'test_covenants':            result = await testCovenantsTool(input as any); break;
      case 'investment_analysis':       result = await investmentAnalysisTool(input as any); break;
      default:
        return { content: JSON.stringify({ error: `Unknown tool: ${name}` }), isError: true };
    }

    // After a successful snapshot_sheet, mark the resolved sheet name so the
    // dispatcher's read-guard above can block subsequent raw reads.
    if (name === 'snapshot_sheet' && result.ok) {
      const sheetName = (result.data as { sheet?: string } | undefined)?.sheet;
      if (sheetName) markSheetSnapshotted(sheetName);
    }

    // Phase C.3 — append a row to the workbook's Practice IQ Log sheet for
    // every write-class tool, when the user has the toggle on. Fire-and-
    // forget — never block the original tool result on the log write.
    if (sessionLogEnabled && LOG_WRITE_TOOLS.has(name)) {
      const summary = describeToolResult(name, result.ok ? result.data : undefined, result.ok, result.error);
      void appendSessionLogRow(name, toolLabel(name), summary, result.ok);
    }

    return {
      content: JSON.stringify(result.ok ? result.data : { error: result.error }),
      isError: !result.ok,
    };
  } catch (e) {
    return {
      content: JSON.stringify({ error: (e as Error).message ?? 'Tool failed' }),
      isError: true,
    };
  }
}
